/* eslint-disable react-hooks/exhaustive-deps */
import { captureException } from "../../constant/sentryConfiguration";
import { v4 } from "uuid";

import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useLocation } from "react-router-dom";

import { getItineraryImageURL } from "../../api/llm/getItineraryImageURL";
import { CHAT_DESTINATION_IMAGE_COUNT, getImageSource } from "../../constant/imageSource";
import {
  cityImageCacheKey,
  cityImagePrompts,
  getCityImageCache,
  loadCityImages,
  setCityImageCache,
} from "../../cache/cityImageCache";
import { ROUTES } from "../../constant";
import { DATA_STREAMING_KEY, LLM_RESPONSE_ERROR } from "../../constant/common";
import { CHAT_DATA, DESTINATION_LIST_DATA, DESTINATION_LIST_DATA_GRID, SELECTED_CHAT_DATA, TEMP_TRIP_DETAILS_DATA } from "../../constant/dummyData";
import { ScreenTypes } from "../../enums";
import { iChatData, iCityDetailsResult, iTripDetails } from "../../types";
import {
  combineDescriptionsByTime,
  getItineraryPriceCityList,
} from "../../utils/topDestinationUtils";
import { updateDaywiseTripDateForMetaData } from "../../utils/bookingUtils";
import { consoleLog } from "../../utils/consoleLog/consoleLog";
import { getStartDateForItinerary } from "../../utils/dateUtils";
import { checkForMissingItineraryDataUtil } from "../../utils/itineraryUtils/checkForMissingItineraryDataUtil";
import { isValidJSON } from "../../utils/itineraryUtils/isValidJson";
import { parseStringToJson } from "../../utils/itineraryUtils/parseStringToJson";
import { removeDuplicateDays } from "../../utils/itineraryUtils/removeDaysDuplicate";
import { updateRateLimitCount } from "../../utils/rateLimit/rateLimit";
import { StateContext } from "../globalContext/context";
import { iGlobalContext } from "../globalContext/interface";
import { useHistoryContext } from "../historyContext/historyContext";
import { iHistoryContext } from "../historyContext/interface";
import { iSocketContext, useSocketContext } from "../socketContext/SocketContext";
import { iDataStreamingContext, iStreamingResponse, OutboundV2Request } from "./dataStreamingContextInterface";
import { getChatId } from "../../utils/chat/chatId";
import { cleanChatTitle, getChatTitle, withChatTitle } from "../../utils/chat/chatTitle";
import {
  maskedEntryIds,
  maskedSwaps,
  readMaskedFrame,
  requestEntryId,
  withMaskedChat,
  withMaskedMessages,
  withMaskedPayload,
} from "../../utils/chat/maskedText";
import { jsonrepair } from "jsonrepair";
import { toastMessage } from "../../utils/ToastProvider";
import { getRegeneratingPrompt } from "../../utils/itineraryUtils/getRegeneratingPrompt";
import { safeParseJSON } from "../../utils/itineraryUtils/safeParseJson";
import { useNavigationUrl } from "../../hooks/useNavigationUrl";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";
import { v2orch, v2orchErr } from "../../utils/v2OrchLog";
import { renderV2BlocksToChatEntry } from "../../utils/v2BlockRenderer";
import { fetchAndStashV2Suggestions } from "../../utils/fetchV2Suggestions";
import { resolveBoolFlag } from "../../utils/appConfig/resolveBoolFlag";
import { APP_CONFIG } from "../../constant/common";
import { isSseTransportEnabled } from "../../utils/sse/sseTransport";
import { initStreamState, applyPart, deriveRenderedText, StreamState } from "../../streaming/partStore";
import { Part, ErrorPart } from "../../streaming/contractB";
import { isContractBPart, isDistinctivePart, isSequencedDone } from "../../streaming/followupIntake";
import { projectItinerary } from "../../streaming/itineraryProjection";
import { projectDestinations, dedupeDestinations } from "../../streaming/destinationsProjection";
import { KNOWN_ITINERARY_TAGS, KNOWN_ACT_TYPE_PREFIXES } from "../../streaming/registry";
import {
  shouldResetOnConnected,
  shouldPreserveStreamedMessage,
  dropEmptyContentMessages,
  isNonEmptyMessageContent,
} from "../../streaming/finalizeGuards";
import { voiceBus } from "../../services/voice/voiceBus";

const DataStreamingContext = createContext({} as iDataStreamingContext);
export const DataStreamingContextProvider = ({ children }: { children: ReactNode }) => {
  const globalContext = useContext(StateContext);
  const {
    setChatData,
    chatData,
    setSelectedChatData,
    isMobile,
    setScreen,
    setFirstScreen,
    screen,
    accessJWT,
    setOpenOverlayScreen,
    setChatInputText,
    selectedChatData,
    setOpenLoaderOverlay,
    openLoaderOverlay,
    promptTemplate,
    destinationListGeneratedGAEventsFlag,
    setDestinationListGeneratedGAEventsFlag,
    appConfig,
    userInfo,
  }: iGlobalContext = globalContext;
  const imageSource = getImageSource(appConfig);
  // FE-side chat-search-v1 kill switch. DELIBERATELY a separate appConfig
  // key (CHAT_SEARCH_V1_ENABLED_FRONTEND) from the Lambda's own
  // CHAT_SEARCH_V1_ENABLED so the two sides can be flipped independently
  // — useful when smoke-testing the Lambda path in dev while the FE
  // stays on legacy. When the FE flag is off, chat_search_* v2_block
  // frames are skipped with a desync warning (see the v2_block handler
  // below). When on, chat_search_* frames are treated as V2 turns and
  // share the renderV2BlocksToChatEntry pipeline.
  const chatSearchV1Enabled = resolveBoolFlag(
    appConfig,
    APP_CONFIG.CHAT_SEARCH_V1_ENABLED_FRONTEND,
  );
  const sseTransportEnabled = isSseTransportEnabled(appConfig);

  const {
    streamingFlagAndResponseType,
    setStreamingFlagAndResponseType,
    lastMessage,
    readyState,
    handleCloseSocketConnection,
    handleOpenSocketConnection,
    turnTokenRef,
    lastChatPayloadRef,
  }: iSocketContext = useSocketContext();

  const location = useLocation();
  const destinationListCityGAEventsCustomObjRef: any = useRef({
    first: "",
    second: "",
    third: "",
    fourth: "",
  });
  /**
   * Web socket
   */
  const streamingTimeout = useRef<any>(null);
  const [regeneratingItinerary, setRegeneratingItinerary] = useState<boolean>(false);

  // V2 orchestrator: accumulate `v2_block` WS frames until the
  // `status:completed` frame arrives, then run the same source-switch the
  // legacy HTTP path used to stamp a single chat entry. See
  // `src/utils/v2BlockRenderer.ts` and `finalizeV2Turn` below.
  const v2BlockBufferRef = useRef<any[]>([]);
  // Stashed by pocContext before `handleOpenSocketConnection`; carries the
  // outbound `messages` array, reqId, and the id of the loading-placeholder
  // chat entry that finalize replaces. Cleared after finalize / on error /
  // on abort.
  const outboundV2RequestRef = useRef<OutboundV2Request | null>(null);
  const lastFinalizedEntryIdRef = useRef<string | null>(null);

  const contractBActiveRef = useRef<boolean>(false);
  const contractBStoreRef = useRef<StreamState | null>(null);
  const contractBItineraryRafRef = useRef<number | null>(null);
  const contractBItineraryEnteredRef = useRef<boolean>(false);
  const contractBItineraryPricedRef = useRef<boolean>(false);
  const contractBItineraryImagedRef = useRef<Set<string>>(new Set<string>());
  const contractBDestRafRef = useRef<number | null>(null);
  const contractBDestImagedRef = useRef<Set<string>>(new Set<string>());
  const contractBDestAppendTargetRef = useRef<string | null>(null);
  const contractBDestAppendBaseRef = useRef<any[]>([]);
  const contractBDestAppendPlaceholderCountRef = useRef<number>(0);
  const contractBChatActiveRef = useRef<boolean>(false);
  const contractBLastResetTurnRef = useRef<number>(-1);

  // chat-search-v1 defers the socket close past `status:completed` until the
  // trailing `suggestions` frame arrives (chips are generated server-side after
  // completion so results aren't delayed — closing the socket at completed would
  // drop that frame). Holds the pending safety-timeout timer so the suggestions
  // handler can cancel it (and close early) and a new turn can clear a stale one.
  const suggestionsWaitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Upper bound on how long we hold the socket open waiting for the chips frame
  // (the backend sends it within a few seconds; this is a leak-guard fallback).
  const SUGGESTIONS_WAIT_MS = 15000;
  function clearSuggestionsWait() {
    if (suggestionsWaitTimerRef.current) {
      clearTimeout(suggestionsWaitTimerRef.current);
      suggestionsWaitTimerRef.current = null;
    }
  }

  const lastProcessedMessageRef = useRef<any>(null);

  // Full itinerary decouple: id of the in-progress chat-path itinerary card in
  // chatData. Set when the card materializes (ITINERARY_SUMMARY, chat path),
  // used by DAILY_PLAN/POST_TEXT to write the streaming itinerary to its OWN
  // card by id (not the shared selectedChatData panel), so the user can switch
  // to a previous card mid-generation without losing the one being generated.
  // Empty string on the card-click path (chatId set) and between turns.
  const streamingItineraryCardIdRef = useRef<string>("");

  function resetV2Buffer(traceId: string | undefined, reason: string) {
    v2BlockBufferRef.current = [];
    v2orch("buffer.reset", { traceId, reason });
  }

  function finalizeV2Turn(parseMessage: any) {
    // Trust whatever the Lambda stamped on the completion frame — if it
    // differs from what the FE sent, the mismatch is logged (below) but the
    // frame's traceId still drives persistence so chat-entry trace IDs stay
    // pinned to what's in CloudWatch.
    const traceId: string | undefined = parseMessage?.traceId;
    const agentKey: string = parseMessage?.agent_key ?? "poc_chat";
    const blocks = v2BlockBufferRef.current;
    const outbound = outboundV2RequestRef.current;
    const pendingEntryId = outbound?.pendingEntryId;
    const feSentTraceId = outbound?.traceId;
    if (!outbound && blocks.length === 0) {
      v2orch("finalize.skip-duplicate", { traceId });
      return;
    }
    if (feSentTraceId && traceId && feSentTraceId !== traceId) {
      v2orchErr("traceId-mismatch", {
        feSent: feSentTraceId,
        lambdaStamped: traceId,
        agentKey,
        note: "Persisting Lambda-stamped traceId; FE-sent value discarded.",
      });
    }

    v2orch("finalize.start", {
      traceId,
      agentKey,
      blocks: blocks.length,
      sourcesSeen: Array.from(new Set(blocks.map((b: any) => b?.source).filter(Boolean))),
      pendingEntryId,
      feSentTraceId,
    });

    // routerKey identifies which DB-driven prompt selected this path. For
    // legacy poc_chat turns it's the existing `poc_orchestration` row. For
    // chat-search-v1 turns it's `chat_search_classifier_system` (the
    // 13-intent Haiku classifier in LLM-function/controllers/chatSearch/
    // router.js). Used for telemetry only — render behavior is identical.
    const isChatSearchV1 =
      typeof agentKey === "string" && agentKey.startsWith("chat_search");
    const routerKey = isChatSearchV1
      ? "chat_search_classifier_system"
      : "poc_orchestration";
    try {
      const result = renderV2BlocksToChatEntry(blocks, {
        traceId,
        agentKey,
        routerKey,
        debugHotel: true,
        callSiteTag: outbound?.caller ?? "ws",
      });

      v2orch("finalize.render", {
        traceId,
        renderPath: result.renderPath,
        lastBlockSource: blocks[blocks.length - 1]?.source ?? null,
        aiAssistantTextLen:
          typeof result.entryPatch.message === "string" ? result.entryPatch.message.length : 0,
      });

      // Build the assistant prompt entry (used both for promptArray on the
      // chat entry and for the suggestions follow-up). Mirrors the legacy
      // HTTP path: [...outbound.messages, { role: "assistant", content }].
      const outboundMessages = dropEmptyContentMessages(outbound?.messages ?? []);
      const promptArray = isNonEmptyMessageContent(result.lastResponseText)
        ? [...outboundMessages, { role: "assistant", content: result.lastResponseText }]
        : [...outboundMessages];

      // Build the final chat entry. If a pending placeholder exists, replace
      // it in-place (preserves the entry id for later patches). Otherwise
      // append a new entry.
      setChatData((prev: iChatData[]) => {
        const targetIdx = pendingEntryId
          ? prev.findIndex((e) => e.id === pendingEntryId)
          : -1;

        const fallback: iChatData = {
          id: pendingEntryId ?? v4(),
          message: LLM_RESPONSE_ERROR.timeOut.message,
          prompt: [],
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
          responseType: "ai_assistant",
        };

        const priorEntry = targetIdx >= 0 ? prev[targetIdx] : undefined;
        const stamped: iChatData = result.isRenderable
          ? ({
              ...(priorEntry ?? ({} as iChatData)),
              id: pendingEntryId ?? v4(),
              prompt: promptArray,
              ...result.entryPatch,
            } as iChatData)
          : fallback;

        if (
          result.isRenderable &&
          priorEntry &&
          shouldPreserveStreamedMessage(result.entryPatch.message, priorEntry.message)
        ) {
          stamped.message = priorEntry.message;
          stamped.prompt = priorEntry.prompt?.length
            ? priorEntry.prompt
            : [...outboundMessages, { role: "assistant", content: priorEntry.message }];
        }

        if (targetIdx >= 0) {
          const next = prev.slice();
          next[targetIdx] = stamped;
          return next;
        }
        return [...prev, stamped];
      });

      v2orch("finalize.stamp", {
        traceId,
        agents: [{ key: agentKey }],
        router: { key: routerKey },
        entryId: pendingEntryId,
        renderable: result.isRenderable,
      });

      // Suggestions: fire-and-forget for renderable assistant turns. Skipped
      // on the fallback path (no content to suggest off of). Chips arrive on a
      // separate HTTP call that can resolve well after the LLM scroll fires, so
      // re-scroll once they land or the chip row gets clipped by the sticky input.
      //
      // chat-search-v1 turns are EXCLUDED here: their chips are generated
      // server-side with the chat_search_suggestions_system prompt and pushed
      // on a standalone WS `suggestions` frame (handled in the WS onmessage
      // below). Firing this HTTP call too would overwrite them with the generic
      // app-backend `poc_suggestions` chips.
      if (result.isRenderable && pendingEntryId) lastFinalizedEntryIdRef.current = pendingEntryId;
      let suggestionsRequested = false;
      if (result.isRenderable && pendingEntryId && outbound?.reqId && !isChatSearchV1 && !sseTransportEnabled) {
        suggestionsRequested = true;
        void fetchAndStashV2Suggestions({
          entryId: pendingEntryId,
          messages: promptArray,
          reqId: outbound.reqId,
          accessJWT,
          userId: userInfo?.id ? String(userInfo.id) : "",
          setChatData,
        });
      }

      v2orch("finalize.done", {
        traceId,
        chatDataLen: undefined, // set after next tick — keep undefined for log compactness
        historySaveScheduled: true, // legacy completed-handler dispatches save
        suggestionsRequested,
      });
    } catch (error: any) {
      v2orchErr("finalize.fail", {
        traceId,
        error: error?.message ?? String(error),
        blocks: blocks.length,
      });
      // Surface the timeout fallback on hard renderer failure so the user
      // isn't stuck on a loading placeholder.
      setChatData((prev: iChatData[]) => {
        if (!pendingEntryId) return prev;
        return prev.map((e) =>
          e.id === pendingEntryId
            ? {
                ...e,
                message: LLM_RESPONSE_ERROR.timeOut.message,
                isLoading: false,
                responseType: "ai_assistant",
              }
            : e,
        );
      });
    }

    resetV2Buffer(traceId, "finalize");
    outboundV2RequestRef.current = null;
  }

  function patchContractBMessage(text: string) {
    const pendingEntryId = outboundV2RequestRef.current?.pendingEntryId;
    if (!pendingEntryId) return;
    setChatData((prev: iChatData[]) =>
      prev.map((entry) =>
        entry.id === pendingEntryId
          ? { ...entry, message: text, isLoading: false, responseType: "ai_assistant", isStreaming: true }
          : entry,
      ),
    );
  }

  function finalizeContractBFollowup() {
    const store = contractBStoreRef.current;
    const outbound = outboundV2RequestRef.current;
    const pendingEntryId = outbound?.pendingEntryId;
    const finalText = store ? deriveRenderedText(store) : "";

    if (pendingEntryId) {
      const outboundMessages = dropEmptyContentMessages(outbound?.messages ?? []);
      const promptArray = isNonEmptyMessageContent(finalText)
        ? [...outboundMessages, { role: "assistant", content: finalText }]
        : [...outboundMessages];
      setChatData((prev: iChatData[]) =>
        prev.map((entry) =>
          entry.id === pendingEntryId
            ? {
                ...entry,
                message: finalText,
                prompt: promptArray,
                isUserMessage: false,
                isLoading: false,
                responseType: "ai_assistant",
                isStreaming: false,
              }
            : entry,
        ),
      );
      lastFinalizedEntryIdRef.current = pendingEntryId;
    }

    if (selectedChatData?.isItineraryStreaming === true) {
      if (!pendingEntryId && isNonEmptyMessageContent(finalText)) {
        setChatData((prev: iChatData[]) => [
          ...prev,
          {
            id: v4(),
            message: finalText,
            prompt: [{ role: "assistant", content: finalText }],
            isUserMessage: false,
            isLoading: false,
            destinationList: [],
            singleSelectedDestination: false,
            responseType: "ai_assistant",
            isStreaming: false,
          },
        ]);
      }
      setSelectedChatData((prev: any) => ({ ...prev, isItineraryStreaming: false }));
      const onItineraryPanel = isMobile
        ? location.pathname === ROUTES.ItineraryDetails
        : screen === ScreenTypes.ItineraryInfo;
      if (onItineraryPanel) {
        if (isMobile) navigateWithParams(ROUTES.TopDestination);
        else {
          setScreen("");
          setFirstScreen(ScreenTypes.topDestination);
        }
      }
      v2orch("contractB.followup.tapFallback", {
        textLen: finalText.length,
        primed: !!pendingEntryId,
        navBack: onItineraryPanel,
      });
    }

    clearTimeout(streamingTimeout.current);
    setOpenOverlayScreen(false);
    setDisableInput(false);
    setChatInputText("");
    updateRateLimitCount();
    setStreamingFlagAndResponseType({
      isStillStreamingFlag: false,
      chatId: "",
      responseType: "",
      showMoreClickFlag: false,
    });

    const saveMethod =
      chatData?.length === 2 && !streamingFlagAndResponseType?.showMoreClickFlag
        ? streamingFlagAndResponseType?.chatId === ""
          ? "POST"
          : "PUT"
        : "PUT";
    setCallSaveHistoryAPIFlag({ flag: true, method: saveMethod });
    v2orch("contractB.finalize", {
      entryId: pendingEntryId,
      textLen: finalText.length,
      saveMethod,
      kind: store?.kind ?? null,
      version: store?.version ?? null,
    });

    contractBStoreRef.current = null;
    outboundV2RequestRef.current = null;
  }

  function handleContractBError(part: ErrorPart) {
    const store = contractBStoreRef.current;
    const pendingEntryId = outboundV2RequestRef.current?.pendingEntryId;
    const partialText = store ? deriveRenderedText(store) : "";
    const errMessage = part?.message || part?.error || LLM_RESPONSE_ERROR.timeOut.message;
    if (pendingEntryId) {
      setChatData((prev: iChatData[]) =>
        prev.map((entry) =>
          entry.id === pendingEntryId
            ? {
                ...entry,
                message: "Hmm, something went wrong on my end.",
                isUserMessage: false,
                isLoading: false,
                responseType: "ai_assistant",
                isStreaming: false,
                isProcessInterrupted: true,
                isErrorMessage: true,
              }
            : entry,
        ),
      );
    }
    clearTimeout(streamingTimeout.current);
    setOpenLoaderOverlay(false);
    setOpenOverlayScreen(false);
    setDisableInput(false);
    setChatInputText("");
    setStreamingFlagAndResponseType({
      isStillStreamingFlag: false,
      chatId: "",
      responseType: "",
      showMoreClickFlag: false,
    });
    v2orchErr("contractB.error", {
      entryId: pendingEntryId,
      message: errMessage,
      hadText: partialText.length > 0,
    });
    releaseItinerarySelection();
    contractBStoreRef.current = null;
    contractBChatActiveRef.current = false;
    outboundV2RequestRef.current = null;
    resetContractBItineraryState();
    voiceBus.requestFailed(lastMessage?.turn);
    handleCloseSocketConnection();
  }

  function resetContractBItineraryState() {
    if (contractBItineraryRafRef.current !== null) {
      clearTimeout(contractBItineraryRafRef.current);
      contractBItineraryRafRef.current = null;
    }
    contractBItineraryEnteredRef.current = false;
    contractBItineraryPricedRef.current = false;
    contractBItineraryImagedRef.current.clear();
    if (contractBDestRafRef.current !== null) {
      clearTimeout(contractBDestRafRef.current);
      contractBDestRafRef.current = null;
    }
    contractBDestImagedRef.current.clear();
    contractBDestAppendTargetRef.current = null;
    contractBDestAppendBaseRef.current = [];
    contractBDestAppendPlaceholderCountRef.current = 0;
  }

  function hasItineraryNode(store: StreamState): boolean {
    return store.rootIds.some((id) => store.byId[id]?.tag === "zv:itinerary");
  }

  function logUnknownItineraryPart(part: any) {
    const tag = part?.tag;
    if (tag && !KNOWN_ITINERARY_TAGS.has(tag)) {
      v2orchErr("contractB.unknown-tag", { tag, id: part?.id, seq: part?.seq, parent: part?.parent });
    }
    if (tag === "zv:act") {
      const actType = typeof part?.attrs?.type === "string" ? part.attrs.type : "";
      const prefix = actType.split(".")[0];
      if (prefix && !KNOWN_ACT_TYPE_PREFIXES.has(prefix)) {
        v2orchErr("contractB.unknown-act-type", { type: actType, prefix, id: part?.id, seq: part?.seq });
      }
    }
  }

  function applyItineraryImagesToProjection(projected: any) {
    const list = Array.isArray(projected?.destinationList) ? projected.destinationList : [];
    return {
      ...projected,
      destinationList: list.map((city: any) => {
        const hit = getCityImageCache(
          cityImageCacheKey(city?.city_name || "", city?.city_country_code || "", cityImagePrompts(city))
        );
        return hit && hit.urls.length > 0 ? { ...city, imageURL: hit.urls[0] } : city;
      }),
    };
  }

  function fetchContractBItineraryImages(projected: any) {
    const list = Array.isArray(projected?.destinationList) ? projected.destinationList : [];
    for (const city of list) {
      const cityName = city?.city_name || "";
      const countryCode = city?.city_country_code || "";
      const prompts = cityImagePrompts(city);
      if (!cityName) continue;
      const key = cityImageCacheKey(cityName, countryCode, prompts);
      if (contractBItineraryImagedRef.current.has(key)) continue;
      contractBItineraryImagedRef.current.add(key);
      if (getCityImageCache(key)) continue;
      loadCityImages(key, CHAT_DESTINATION_IMAGE_COUNT, () =>
        getItineraryImageURL(
          accessJWT,
          cityName,
          countryCode || undefined,
          CHAT_DESTINATION_IMAGE_COUNT,
          imageSource,
          prompts
        )
      )
        .then((entry) => {
          if (entry) scheduleContractBItinerary();
        })
        .catch(() => {});
    }
  }

  function fireContractBItineraryPriceOnce(projected: any) {
    if (contractBItineraryPricedRef.current) return;
    const list = Array.isArray(projected?.destinationList) ? projected.destinationList : [];
    if (list.length === 0) return;
    contractBItineraryPricedRef.current = true;
    const pricePayload: any = { cities: getItineraryPriceCityList(list) };
    setCallPriceAPIFlag({ false: true, payload: pricePayload });
  }

  function flushContractBItinerary(finalizing: boolean) {
    const store = contractBStoreRef.current;
    const cardId = outboundV2RequestRef.current?.pendingEntryId;
    if (!store || !cardId) return;
    const projected = applyItineraryImagesToProjection(projectItinerary(store));
    setSelectedChatData((prev: any) => ({ ...prev, ...projected, id: cardId, isItineraryStreaming: !finalizing }));
    setChatData((prev: iChatData[]) =>
      prev.map((entry) =>
        entry.id === cardId
          ? { ...entry, ...projected, id: cardId, isUserMessage: false, isLoading: !finalizing, isItineraryStreaming: !finalizing }
          : entry,
      ),
    );
    fetchContractBItineraryImages(projected);
    fireContractBItineraryPriceOnce(projected);
  }

  function scheduleContractBItinerary() {
    if (contractBItineraryRafRef.current !== null) return;
    contractBItineraryRafRef.current = window.setTimeout(() => {
      contractBItineraryRafRef.current = null;
      flushContractBItinerary(false);
    }, 16);
  }

  function enterContractBItineraryModeOnce() {
    if (contractBItineraryEnteredRef.current) return;
    contractBItineraryEnteredRef.current = true;
    const existingCardId = outboundV2RequestRef.current?.pendingEntryId;
    const cardId = existingCardId ?? v4();
    if (!existingCardId) {
      outboundV2RequestRef.current = {
        messages: [],
        reqId: v4(),
        traceId: cardId.slice(0, 8),
        pendingEntryId: cardId,
        caller: "cardTap",
      };
    }
    if (outboundV2RequestRef.current) outboundV2RequestRef.current.selectionBefore = selectedChatData;
    streamingItineraryCardIdRef.current = cardId;
    setSelectedChatData((prev: any) => ({ ...prev, destinationList: [], isItineraryStreaming: true }));
    setStreamingFlagAndResponseType((prev: any) => ({ ...prev, booking: false }));
    setChatInputText("");
    if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.ItineraryDetails);
    else {
      setScreen(ScreenTypes.ItineraryInfo);
      setFirstScreen(ScreenTypes.topDestination);
    }
    setOpenLoaderOverlay(false);
    v2orch("contractB.itinerary.enter", { entryId: cardId });
  }

  function releaseItinerarySelection() {
    const outbound = outboundV2RequestRef.current;
    const before = outbound?.selectionBefore;
    if (!outbound || !before) return;
    outbound.selectionBefore = undefined;
    resetContractBItineraryState();
    setSelectedChatData((prev: any) => (prev?.isItineraryStreaming ? { ...before, isItineraryStreaming: false } : prev));
    v2orch("contractB.itinerary.release", { entryId: outbound.pendingEntryId, selectionId: before.id || null });
  }

  function finalizeContractBItinerary() {
    if (!contractBItineraryEnteredRef.current) {
      finalizeContractBFollowup();
      resetContractBItineraryState();
      return;
    }
    const store = contractBStoreRef.current;
    const outbound = outboundV2RequestRef.current;
    const cardId = outbound?.pendingEntryId;
    flushContractBItinerary(true);

    const projected = store ? projectItinerary(store) : null;
    const assistantText = [projected?.preText, projected?.complete_itinerary_description, projected?.postText]
      .filter((t) => typeof t === "string" && t.length > 0)
      .join("\n\n");
    if (cardId) {
      const outboundMessages = dropEmptyContentMessages(outbound?.messages ?? []);
      const promptArray = isNonEmptyMessageContent(assistantText)
        ? [...outboundMessages, { role: "assistant", content: assistantText }]
        : [...outboundMessages];
      setChatData((prev: iChatData[]) =>
        prev.map((entry) => (entry.id === cardId ? { ...entry, prompt: promptArray } : entry)),
      );
      lastFinalizedEntryIdRef.current = cardId;
    }

    clearTimeout(streamingTimeout.current);
    setOpenOverlayScreen(false);
    setDisableInput(false);
    setChatInputText("");
    updateRateLimitCount();
    setStreamingFlagAndResponseType({
      isStillStreamingFlag: false,
      chatId: "",
      responseType: "",
      showMoreClickFlag: false,
    });

    const saveMethod =
      chatData?.length === 2 && !streamingFlagAndResponseType?.showMoreClickFlag
        ? streamingFlagAndResponseType?.chatId === ""
          ? "POST"
          : "PUT"
        : "PUT";
    setCallSaveHistoryAPIFlag({ flag: true, method: saveMethod });
    v2orch("contractB.finalize", {
      entryId: cardId,
      kind: store?.kind ?? null,
      version: store?.version ?? null,
      cities: projected?.destinationList?.length ?? 0,
      seqGap: store?.seqGap ?? false,
      traceId: outbound?.traceId,
    });

    contractBStoreRef.current = null;
    outboundV2RequestRef.current = null;
    streamingItineraryCardIdRef.current = "";
    resetContractBItineraryState();
  }

  function applyDestinationImagesToProjection(projected: any) {
    const list = Array.isArray(projected?.destinationList) ? projected.destinationList : [];
    return {
      ...projected,
      destinationList: list.map((card: any) => {
        const hit = getCityImageCache(
          cityImageCacheKey(card?.destination_city || "", card?.country_code || "", cityImagePrompts(card))
        );
        return hit && hit.urls.length > 0 ? { ...card, imageURL: hit.urls[0] } : card;
      }),
    };
  }

  function fetchContractBDestinationImages(projected: any) {
    const list = Array.isArray(projected?.destinationList) ? projected.destinationList : [];
    for (const card of list) {
      const cityName = card?.destination_city || "";
      const countryCode = card?.country_code || "";
      const prompts = cityImagePrompts(card);
      if (!cityName) continue;
      const key = cityImageCacheKey(cityName, countryCode, prompts);
      if (contractBDestImagedRef.current.has(key)) continue;
      contractBDestImagedRef.current.add(key);
      if (getCityImageCache(key)) continue;
      loadCityImages(key, CHAT_DESTINATION_IMAGE_COUNT, () =>
        getItineraryImageURL(
          accessJWT,
          cityName,
          countryCode || undefined,
          CHAT_DESTINATION_IMAGE_COUNT,
          imageSource,
          prompts
        )
      )
        .then((entry) => {
          if (entry) scheduleContractBDestinations();
        })
        .catch(() => {});
    }
  }

  function captureAppendTargetIfNeeded() {
    if (contractBDestAppendTargetRef.current) return;
    if (outboundV2RequestRef.current?.pendingEntryId) return;
    setChatData((prev: iChatData[]) => {
      for (let i = prev.length - 1; i >= 0; i--) {
        const list = Array.isArray(prev[i]?.destinationList) ? (prev[i].destinationList as any[]) : [];
        const real = list.filter((c: any) => c?.destination_name);
        if (real.length > 0) {
          contractBDestAppendTargetRef.current = prev[i].id ?? null;
          contractBDestAppendBaseRef.current = real;
          contractBDestAppendPlaceholderCountRef.current = Math.max(0, list.length - real.length);
          break;
        }
      }
      return prev;
    });
  }

  function flushContractBDestinations(finalizing: boolean) {
    const store = contractBStoreRef.current;
    const appendTarget = contractBDestAppendTargetRef.current;
    const cardId = appendTarget || outboundV2RequestRef.current?.pendingEntryId;
    if (!store || !cardId) return;
    const projected = applyDestinationImagesToProjection(projectDestinations(store));
    if (appendTarget) {
      const base = contractBDestAppendBaseRef.current ?? [];
      const deduped = dedupeDestinations([...base, ...projected.destinationList]);
      const newShown = deduped.length - base.length;
      const expected = contractBDestAppendPlaceholderCountRef.current || 2;
      const loaderCount = finalizing ? 0 : Math.max(0, expected - newShown);
      const loaders = Array.from({ length: loaderCount }, (_, i) => ({
        ...DESTINATION_LIST_DATA_GRID,
        id: `__dest_loader_${i}`,
      }));
      const merged = [...deduped, ...loaders];
      setChatData((prev: iChatData[]) =>
        prev.map((entry) =>
          entry.id === cardId
            ? {
                ...entry,
                destinationList: merged,
                postText: projected.postText || entry.postText,
                isUserMessage: false,
                singleSelectedDestination: false,
                isLoading: false,
              }
            : entry,
        ),
      );
      fetchContractBDestinationImages({ destinationList: projected.destinationList });
      if (finalizing && deduped.length <= base.length) {
        toastMessage.info("No new destinations to add right now.");
      }
      return;
    }
    const hasContent = projected.preText.length > 0 || projected.destinationList.length > 0;
    setChatData((prev: iChatData[]) =>
      prev.map((entry) =>
        entry.id === cardId
          ? { ...entry, ...projected, id: cardId, isUserMessage: false, isLoading: finalizing ? false : !hasContent, isStreaming: !finalizing }
          : entry,
      ),
    );
    fetchContractBDestinationImages(projected);
  }

  function scheduleContractBDestinations() {
    if (contractBDestRafRef.current !== null) return;
    contractBDestRafRef.current = window.setTimeout(() => {
      contractBDestRafRef.current = null;
      flushContractBDestinations(false);
    }, 16);
  }

  function finalizeContractBDestinations() {
    const store = contractBStoreRef.current;
    const outbound = outboundV2RequestRef.current;
    const appendTarget = contractBDestAppendTargetRef.current;
    const cardId = appendTarget || outbound?.pendingEntryId;
    flushContractBDestinations(true);

    const projected = store ? projectDestinations(store) : null;
    const assistantText = [projected?.preText, projected?.postText]
      .filter((t) => typeof t === "string" && t.length > 0)
      .join("\n\n");
    if (cardId) {
      if (!appendTarget) {
        const outboundMessages = dropEmptyContentMessages(outbound?.messages ?? []);
        const promptArray = isNonEmptyMessageContent(assistantText)
          ? [...outboundMessages, { role: "assistant", content: assistantText }]
          : [...outboundMessages];
        setChatData((prev: iChatData[]) =>
          prev.map((entry) => (entry.id === cardId ? { ...entry, prompt: promptArray } : entry)),
        );
      }
      lastFinalizedEntryIdRef.current = cardId;
    }

    clearTimeout(streamingTimeout.current);
    setOpenOverlayScreen(false);
    setDisableInput(false);
    setChatInputText("");
    updateRateLimitCount();
    setStreamingFlagAndResponseType({
      isStillStreamingFlag: false,
      chatId: "",
      responseType: "",
      showMoreClickFlag: false,
    });

    const saveMethod =
      chatData?.length === 2 && !streamingFlagAndResponseType?.showMoreClickFlag
        ? streamingFlagAndResponseType?.chatId === ""
          ? "POST"
          : "PUT"
        : "PUT";
    setCallSaveHistoryAPIFlag({ flag: true, method: saveMethod });
    v2orch("contractB.finalize", {
      entryId: cardId,
      kind: store?.kind ?? null,
      version: store?.version ?? null,
      destinations: projected?.destinationList?.length ?? 0,
      append: !!appendTarget,
    });

    contractBStoreRef.current = null;
    outboundV2RequestRef.current = null;
    resetContractBItineraryState();
  }

  function applyContractBChatCards(parseMessage: any) {
    const blocks = v2BlockBufferRef.current;
    const traceId: string | undefined = parseMessage?.traceId;
    const agentKey: string = parseMessage?.agent_key ?? "poc_chat";
    const targetId = lastFinalizedEntryIdRef.current;
    if (!targetId || blocks.length === 0) {
      v2orch("contractB.chat.cards-skip", {
        traceId,
        targetId,
        blocks: blocks.length,
      });
      resetV2Buffer(traceId, "contractB-chat-cards");
      return;
    }
    try {
      const result = renderV2BlocksToChatEntry(blocks, {
        traceId,
        agentKey,
        routerKey: "poc_orchestration",
        debugHotel: true,
        callSiteTag: "contractB-chat",
      });
      const cardKeys = [
        "flightResponse",
        "hotelResponse",
        "activityResponse",
        "flightSearchRequest",
        "hotelSearchRequest",
        "resultsGeneratedAt",
      ] as const;
      const cardPatch: Record<string, any> = {};
      for (const k of cardKeys) {
        const v = (result.entryPatch as any)?.[k];
        if (v !== undefined) cardPatch[k] = v;
      }
      for (const k of ["agents", "router", "traceId"] as const) {
        const v = (result.entryPatch as any)?.[k];
        if (v !== undefined) cardPatch[k] = v;
      }
      const patchedResponseType = (result.entryPatch as any)?.responseType;
      const hasCards =
        cardPatch.flightResponse !== undefined ||
        cardPatch.hotelResponse !== undefined ||
        cardPatch.activityResponse !== undefined;
      v2orch("contractB.chat.cards", {
        traceId,
        entryId: targetId,
        renderPath: result.renderPath,
        keys: Object.keys(cardPatch),
        hasCards,
      });
      if (hasCards) {
        setChatData((prev: iChatData[]) =>
          prev.map((e) =>
            e.id === targetId
              ? ({
                  ...e,
                  ...cardPatch,
                  responseType:
                    patchedResponseType && patchedResponseType !== "ai_assistant"
                      ? patchedResponseType
                      : e.responseType,
                  isLoading: false,
                } as iChatData)
              : e,
          ),
        );
      }
    } catch (error: any) {
      v2orchErr("contractB.chat.cards-fail", {
        traceId,
        error: error?.message ?? String(error),
        blocks: blocks.length,
      });
    }
    resetV2Buffer(traceId, "contractB-chat-cards");
  }

  function stampResponder(entryId: string, agentKey: string, traceId: string | undefined) {
    const routerKey = agentKey.startsWith("chat_search") ? "chat_search_classifier_system" : "poc_orchestration";
    setChatData((prev: iChatData[]) =>
      prev.map((entry) =>
        entry.id === entryId
          ? {
              ...entry,
              agents: [{ key: agentKey }],
              router: { key: routerKey },
              ...(traceId ? { traceId } : {}),
            }
          : entry,
      ),
    );
    v2orch("contractB.stamp", { traceId, agentKey, entryId });
  }

  function handleContractBFrame(part: Part) {
    if (isDistinctivePart(part)) {
      contractBActiveRef.current = true;
      const store = contractBStoreRef.current ?? initStreamState();
      contractBStoreRef.current = store;
      applyPart(store, part);
      if (part.type === "open") {
        v2orch("contractB.open", {
          id: part.id,
          tag: part.tag,
          seq: part.seq,
          kind: store.kind,
          version: store.version,
        });
      }
      if (store.kind === "itinerary") {
        if (part.type === "open" || part.type === "item") logUnknownItineraryPart(part);
        const introClosed = part.type === "close" && store.byId[part.id]?.tag === "zv:intro";
        if (contractBItineraryEnteredRef.current || hasItineraryNode(store) || introClosed) {
          enterContractBItineraryModeOnce();
          scheduleContractBItinerary();
        } else if (part.type !== "open") {
          patchContractBMessage(deriveRenderedText(store));
        }
      } else if (store.kind === "destinations") {
        captureAppendTargetIfNeeded();
        scheduleContractBDestinations();
      } else {
        if (store.kind === "chat") contractBChatActiveRef.current = true;
        if (store.kind === "chat") {
          const dbgRootIds = store.rootIds;
          const dbgDupRoot = dbgRootIds.length !== new Set(dbgRootIds).size;
          const dbgPart = part as { seq: number; type: string; id: string };
          if (dbgPart.type !== "delta" || dbgRootIds.length > 1 || dbgDupRoot) {
            console.log("[dbg-doublereveal]", {
              seq: dbgPart.seq,
              type: dbgPart.type,
              partId: dbgPart.id,
              rootIds: dbgRootIds.join(","),
              dupRoot: dbgDupRoot,
              rootLens: dbgRootIds.map((r) => store.byId[r]?.text?.length ?? 0).join(","),
              derivedLen: deriveRenderedText(store).length,
            });
          }
        }
        if (part.type !== "open") patchContractBMessage(deriveRenderedText(store));
      }
      return;
    }
    if (part.type === "error") {
      const store = contractBStoreRef.current;
      if (store) applyPart(store, part);
      handleContractBError(part);
      return;
    }
    if (part.type === "done") {
      const store = contractBStoreRef.current;
      if (!store) {
        if (isSequencedDone(part) && outboundV2RequestRef.current?.pendingEntryId) {
          v2orchErr("contractB.empty-reply", { seq: part.seq, entryId: outboundV2RequestRef.current.pendingEntryId });
          handleContractBError({ type: "error", seq: part.seq, message: "empty reply" });
        }
        return;
      }
      applyPart(store, part);
      if (store.kind === "itinerary") finalizeContractBItinerary();
      else if (store.kind === "destinations") finalizeContractBDestinations();
      else finalizeContractBFollowup();
    }
  }

  // NOTE: chat-search-v1 turns no longer progressively paint result cards as
  // `v2_block` frames arrive. The buffer accumulates in the WS handler and
  // renders ONCE at the terminal `status:completed` via finalizeV2Turn (same
  // as the legacy poc_chat path). Showing a half-built result list while the
  // turn was still "Finalizing your picks…" was confusing, so results are held
  // until completion; the in-flight cue is the Pusher search-progress worklist
  // (SearchProgress in ChatContentNew), which stays until finalize flips
  // isLoading→false. (The former renderV2Progress helper was removed here.)

  function startSocketStreamingTimer() {
    /**
     * Reset chat in case of no response
     */
    streamingTimeout.current = setTimeout(() => {
      setOpenLoaderOverlay(false);
      handleCloseSocketConnection();
      setChatInputText("");
      if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
      else {
        setScreen("");
        setFirstScreen(ScreenTypes.topDestination);
      }
      if (streamingFlagAndResponseType?.isStillStreamingFlag === false) {
        setDisableInput(false);
        handleCloseSocketConnection();
        return;
      }
      if (streamingFlagAndResponseType?.chatId === "" && streamingFlagAndResponseType?.showMoreClickFlag === false)
        setChatData((prev: any) => [
          ...prev.slice(0, -1),
          {
            id: v4(),
            message: LLM_RESPONSE_ERROR.timeOut.message,
            prompt: [],
            isUserMessage: false,
            isLoading: false,
            destinationList: [],
            singleSelectedDestination: false,
          },
        ]);
      else if (streamingFlagAndResponseType?.showMoreClickFlag === true) {
        setChatData((prev: any) =>
          prev.map((ele: iChatData) => ({
            ...ele,
            destinationList: ele?.destinationList?.filter(
              (eleDestList: iCityDetailsResult) => eleDestList?.destination_name
            ),
          }))
        );
        setChatData((prev: any) => [
          ...prev,
          {
            id: v4(),
            message: LLM_RESPONSE_ERROR.timeOut.message,
            prompt: [],
            isUserMessage: false,
            isLoading: false,
            destinationList: [],
            singleSelectedDestination: false,
          },
        ]);
      }
      setChatData((prev: any) => prev.filter((ele: any) => !ele?.isLoading));
      streamingItineraryCardIdRef.current = "";
      setStreamingFlagAndResponseType({
        isStillStreamingFlag: false,
        chatId: "",
        responseType: "",
        showMoreClickFlag: false,
      });
      setDisableInput(false);
      handleCloseSocketConnection();
    }, 170000);
  }
  function applyMaskedFrame(frame: any, turn: any) {
    const masked = readMaskedFrame(frame, turn?.requestType === "voiceExchange");
    if (!masked) {
      v2orchErr("frame.masked.ignored", {
        traceId: turn?.traceId,
        userInput: typeof frame?.userInput,
        voiceReply: typeof frame?.voiceReply,
      });
      return;
    }
    const swaps = maskedSwaps(turn?.sent, masked);
    const outbound = outboundV2RequestRef.current;
    const targets = { requestId: requestEntryId(chatData, outbound, turn?.sent), replyTraceId: turn?.traceId ?? null };
    const entryIds = maskedEntryIds(chatData, swaps, targets);
    v2orch("frame.masked", {
      traceId: turn?.traceId,
      requestType: turn?.requestType,
      userInput: masked.userInput,
      voiceReply: masked.voiceReply,
      swaps: swaps.length,
      entries: entryIds.length,
    });
    if (swaps.length === 0) return;
    setChatData((prev: iChatData[]) => withMaskedChat(prev, swaps, targets));
    if (outbound) outboundV2RequestRef.current = { ...outbound, messages: withMaskedMessages(outbound.messages, swaps) };
    lastChatPayloadRef.current = withMaskedPayload(lastChatPayloadRef.current, swaps);
    voiceBus.requestMasked({ traceId: turn?.traceId ?? "" });
    if (entryIds.length > 0 && !streamingFlagAndResponseType?.isStillStreamingFlag) {
      setCallSaveHistoryAPIFlag((prev) => ({ flag: true, method: prev.flag ? prev.method : "PUT" }));
    }
  }
  useEffect(() => {
    if (streamingFlagAndResponseType?.isStillStreamingFlag === false) {
      clearTimeout(streamingTimeout.current);
    }
  }, [streamingFlagAndResponseType]);
  useEffect(() => {
    if (
      streamingFlagAndResponseType?.socketOpenFrom === "ACTIVITY_DETAILS" ||
      streamingFlagAndResponseType?.socketOpenFrom === "ONBOARDING" ||
      // lares chat-refine frames are consumed by useLaresChat's own effect; keep
      // them out of the askZoya/poc dispatch (same isolation as ACTIVITY_DETAILS).
      streamingFlagAndResponseType?.socketOpenFrom === "LARES_ALERT"
    )
      return;
    if (isMobile) {
      if (
        !(
          location.pathname === "/" ||
          location.pathname === ROUTES.TopDestination ||
          location.pathname === ROUTES.ItineraryDetails ||
          location.pathname === ROUTES.NewTrip ||
          location.pathname === ROUTES.BookingLoader ||
          location.pathname === ROUTES.onBoarding ||
          //
          location.pathname === ROUTES.PortalChat
        )
      ) {
        return;
      }
    } else {
      if (screen === ScreenTypes.newBooking) return;
    }
    if (lastMessage === lastProcessedMessageRef.current) return;
    lastProcessedMessageRef.current = lastMessage;
    if (isValidJSON(lastMessage?.data)) {
      const parseMessage = safeParseJSON(lastMessage?.data);

      // Heartbeat frames are emitted by LLM-function every 15s while a Lambda
      // invocation is in flight, purely to keep the WS warm past API Gateway /
      // ALB / CloudFront / FE-watchdog idle timeouts. They carry no payload
      // we care about — short-circuit before the v2orch logging and dispatch
      // so they don't pollute the trace log or trigger any handler branches.
      if (parseMessage?.type === "heartbeat") return;

      const frameTurn = lastMessage?.turn;
      if (parseMessage?.type === "voice") {
        voiceBus.voiceFrame(parseMessage, contractBStoreRef.current?.kind ?? null);
        return;
      }
      if (parseMessage?.type === "masked") {
        applyMaskedFrame(parseMessage, frameTurn);
        return;
      }
      if (frameTurn?.requestType === "voiceExchange" && parseMessage?.type !== "title" && parseMessage?.type !== "suggestions") {
        if (parseMessage?.type === "error") {
          v2orchErr("voiceExchange.error", { traceId: frameTurn.traceId, message: parseMessage?.message ?? parseMessage?.error });
          handleCloseSocketConnection();
        }
        return;
      }

      if (isContractBPart(parseMessage, contractBActiveRef.current)) {
        handleContractBFrame(parseMessage as Part);
        return;
      }

      // Lambda stamps `agent_key` + `traceId` on every outbound frame when
      // orchestrator-routing is on (origin allowlisted + DB flag enabled).
      // Pre-orchestrator clients see the same frames without those fields.
      const v2FrameTraceId: string | undefined = parseMessage?.traceId;
      const v2FrameAgentKey: string | undefined = parseMessage?.agent_key;
      const feSentTraceIdForFrame = outboundV2RequestRef.current?.traceId;
      v2orch("frame.recv", {
        traceId: v2FrameTraceId,
        type: parseMessage?.type,
        status: parseMessage?.status,
        agentKey: v2FrameAgentKey,
        section: parseMessage?.section,
        // Surface the error message + any reason field so the failure mode is
        // visible from the log alone (no inspect-network-tab required).
        message: parseMessage?.message,
        reason: parseMessage?.reason,
        dataKeys:
          parseMessage?.data && typeof parseMessage.data === "object"
            ? Object.keys(parseMessage.data)
            : null,
      });
      // Mismatch detection on EVERY traced frame — not just completion. If the
      // FE-generated traceId is being ignored by the Lambda (PR #13 not
      // deployed or field-name regression), the first mismatched frame surfaces
      // it. Logged once per frame.
      if (
        feSentTraceIdForFrame &&
        v2FrameTraceId &&
        feSentTraceIdForFrame !== v2FrameTraceId
      ) {
        v2orchErr("traceId-mismatch", {
          frameType: parseMessage?.type,
          feSent: feSentTraceIdForFrame,
          lambdaStamped: v2FrameTraceId,
          agentKey: v2FrameAgentKey,
          note: "Frame traceId differs from FE-sent value. Most likely Lambda PR #13 (FE traceId forwarding) isn't deployed yet; Lambda is minting its own. Persistence still uses Lambda-stamped value.",
        });
      }

      // ON RECEVING FIRST MESSAGE
      if (parseMessage?.type === "status" && parseMessage?.status === "connected") {
        if (shouldResetOnConnected(turnTokenRef.current, contractBLastResetTurnRef.current)) {
          contractBLastResetTurnRef.current = turnTokenRef.current;
          // Decouple: clear any stale streaming-card id from a prior turn so a
          // chat-path id can't leak into a later card-click stream.
          streamingItineraryCardIdRef.current = "";
          contractBActiveRef.current = false;
          contractBChatActiveRef.current = false;
          contractBStoreRef.current = null;
          clearSuggestionsWait();
          resetContractBItineraryState();
          // Defensive reset — turn boundary. If a prior turn aborted mid-stream
          // and left blocks in the buffer, drop them so they don't leak into
          // the next finalize.
          if (v2BlockBufferRef.current.length > 0) {
            resetV2Buffer(v2FrameTraceId, "connected");
          }
        }
        setStreamingFlagAndResponseType((prev: any) => ({
          ...prev,
          isStillStreamingFlag: true,
          responseType: "",
        }));
      }

      // Dump the entire error frame body — fields outside the standard
      // {type,status,traceId,agent_key} shape may hide the upstream error
      // reason (e.g. Lambda might use `error`, `detail`, `appBackendStatus`,
      // etc. instead of `message`). One-shot per error frame.
      if (parseMessage?.type === "error") {
        v2orchErr("error-frame-full", {
          traceId: v2FrameTraceId,
          agentKey: v2FrameAgentKey,
          allKeys: Object.keys(parseMessage),
          full: parseMessage,
        });
      }

      // Defensive reset — error frames mid-V2-turn discard buffered blocks
      // so they don't leak into the next finalize. The error itself is
      // handled by handleConversationStreamEvents below.
      if (parseMessage?.type === "error" && v2BlockBufferRef.current.length > 0) {
        resetV2Buffer(v2FrameTraceId, "error");
        if (outboundV2RequestRef.current) {
          v2orchErr("turn-error-during-v2", {
            traceId: v2FrameTraceId,
            agentKey: v2FrameAgentKey,
            message: parseMessage?.message,
          });
          outboundV2RequestRef.current = null;
        }
      }

      // V2 orchestrator path: poc_chat-routed turns arrive as a sequence of
      // `v2_block` frames terminated by a `status:completed` w/
      // agent_key:"poc_chat". chat-search-v1 also uses the same v2_block
      // envelope with agent_key:"chat_search*", gated by the FE
      // CHAT_SEARCH_V1_ENABLED_FRONTEND appConfig flag. Accumulate blocks;
      // finalize at the end.
      //
      // Defensive: when chat_search frames arrive while the FE flag is OFF,
      // SKIP buffering and emit a loud warning. Without this skip, the
      // blocks would pile up but `finalizeV2Turn` would never fire (the
      // isV2 check below also requires the flag), leaving the assistant
      // bubble stuck in a loading state. Skip + warning makes the desync
      // visible in monitoring instead of silently breaking the chat.
      const v2FrameIsChatSearch =
        typeof v2FrameAgentKey === "string" && v2FrameAgentKey.startsWith("chat_search");
      if (parseMessage?.type === "v2_block" && v2FrameIsChatSearch && !chatSearchV1Enabled) {
        v2orchErr("chat-search-flag-desync", {
          traceId: v2FrameTraceId,
          agentKey: v2FrameAgentKey,
          note:
            "Lambda emitted a chat_search v2_block but the FE appConfig " +
            "CHAT_SEARCH_V1_ENABLED_FRONTEND flag is off. Frame ignored. " +
            "Set the appConfig row 'CHAT_SEARCH_V1_ENABLED_FRONTEND' to " +
            "'true' (or disable the Lambda's CHAT_SEARCH_V1_ENABLED) to " +
            "keep them in lockstep.",
        });
        return;
      }
      if (parseMessage?.type === "v2_block") {
        // A new turn's content is arriving while we were still holding the
        // socket open for the PREVIOUS turn's suggestions frame → that turn is
        // over; drop the stale close-timer so it can't close this new turn's
        // socket. (The new turn owns the socket now and will close it itself.)
        clearSuggestionsWait();
        const block = parseMessage?.data ?? {};
        v2BlockBufferRef.current.push(block);
        v2orch("frame.v2_block", {
          traceId: v2FrameTraceId,
          blockType: block?.type,
          source: block?.source,
          textLen: typeof block?.text === "string" ? block.text.length : null,
          // Preview of the actual block content — for ai_assistant blocks this
          // is the LLM's prose (including any bullet glyphs); for hotel/flight
          // blocks it's the JSON tool result.
          textPreview: typeof block?.text === "string" ? block.text.slice(0, 200) : undefined,
          name: block?.name ?? null,
          bufferSize: v2BlockBufferRef.current.length,
        });
        v2orch("buffer.push", {
          traceId: v2FrameTraceId,
          size: v2BlockBufferRef.current.length,
          blockType: block?.type,
          source: block?.source,
        });

        // Results are intentionally NOT painted mid-stream. chat-search-v1
        // frames buffer here and render ONCE at the terminal status:completed
        // (finalizeV2Turn) — same as the legacy poc_chat path. Streaming the
        // result cards in while the turn was still "Finalizing your picks…"
        // showed a half-built list before the turn completed; hold everything
        // until completion. The in-flight cue is the Pusher search-progress
        // worklist (SearchProgress in ChatContentNew), which stays visible
        // until finalize flips isLoading→false.
        return;
      }

      if (parseMessage?.type === "title") {
        const title = cleanChatTitle(parseMessage?.data);
        v2orch("frame.title", {
          traceId: v2FrameTraceId,
          len: typeof parseMessage?.data === "string" ? parseMessage.data.length : null,
          requestType: frameTurn?.requestType,
          stillStreaming: !!streamingFlagAndResponseType?.isStillStreamingFlag,
          historyId: currentHistoryId || null,
        });
        if (title && title !== getChatTitle(chatData)) {
          setChatData((prev: iChatData[]) => withChatTitle(prev, title));
          if (currentHistoryId) patchHistoryTitle(currentHistoryId, title);
          if (!streamingFlagAndResponseType?.isStillStreamingFlag) {
            setCallSaveHistoryAPIFlag((prev) => ({ flag: true, method: prev.flag ? prev.method : "PUT" }));
          }
        }
        return;
      }

      // chat-search-v1 suggestion chips arrive on their own frame, emitted by
      // the LLM-function orchestrator AFTER status:completed (so generating
      // them never delays result rendering). Generated with the
      // chat_search_suggestions_system prompt (BOOKING / NAMED-REPLACEMENT /
      // SPECIFIC-DATE bans) — unlike the app-backend poc_suggestions HTTP path,
      // which finalizeV2Turn skips for chat-search turns. Stash the chips onto
      // the just-finalized entry, matched by the turn's traceId (finalize
      // stamps it), then re-scroll so the chip row isn't clipped by the sticky
      // input.
      if (parseMessage?.type === "suggestions") {
        const rawChips = Array.isArray(parseMessage?.data) ? parseMessage.data : parseMessage?.suggestions;
        const chips: string[] = Array.isArray(rawChips)
          ? rawChips.filter((s: any) => typeof s === "string" && s.trim().length > 0)
          : [];
        const sugTraceId: string | undefined =
          parseMessage?.traceId ?? (frameTurn?.requestType === "voiceExchange" ? frameTurn.traceId : undefined);
        v2orch("frame.suggestions", { traceId: sugTraceId, count: chips.length });
        if (chips.length > 0) {
          setChatData((prev: iChatData[]) => {
            const byTrace = !!sugTraceId && prev.some((e) => e.traceId === sugTraceId);
            return prev.map((e) =>
              (byTrace ? e.traceId === sugTraceId : e.id === lastFinalizedEntryIdRef.current)
                ? { ...e, v2Suggestions: chips }
                : e,
            );
          });
        }
        // This frame is the turn's terminal signal (always sent for a v2 turn,
        // even with an empty array). The socket was held open past
        // status:completed to receive it — now it's safe to close.
        clearSuggestionsWait();
        handleCloseSocketConnection();
        contractBChatActiveRef.current = false;
        return;
      }

      if (
        parseMessage?.message !== "Endpoint request timed out" &&
        (streamingFlagAndResponseType?.isStillStreamingFlag || parseMessage?.type === "error")
      ) {
        if (streamingFlagAndResponseType?.showMoreClickFlag && parseMessage?.type !== "error")
          handleShowmoreEvents(parseMessage);
        else {
          // Surface the actual text/data on every legacy frame so we can see
          // what the LLM is emitting — this is the path bullet-bug-style
          // messages travel through (FOLLOW_UP stream-text chunks concat
          // into the final assistant `message` field).
          const dataStr = typeof parseMessage?.data === "string" ? parseMessage.data : undefined;
          v2orch("frame.legacy", {
            traceId: v2FrameTraceId,
            agentKey: v2FrameAgentKey,
            type: parseMessage?.type,
            section: parseMessage?.section,
            dataLen: dataStr ? dataStr.length : null,
            dataPreview: dataStr ? dataStr.slice(0, 200) : undefined,
          });
          handleConversationStreamEvents(parseMessage, streamingFlagAndResponseType);
        }
      }

      // ON RECEIVING LAST MESSAGE
      if (parseMessage?.type === "status" && parseMessage?.status === "completed" && parseMessage?.data !== "[DONE]") {
        // V2 detection accepts legacy `poc_chat` always; chat_search_*
        // keys (chat-search-v1 alignment) only when the FE appConfig flag
        // is on so the FE can independently roll back if the Lambda flag
        // was flipped prematurely. When the flag is off, chat_search_*
        // frames fall through to the legacy non-V2 handler — safe
        // degradation, no crash, just plainer rendering until the FE is
        // ready to accept the new path.
        const isV2 =
          v2FrameAgentKey === "poc_chat" ||
          (chatSearchV1Enabled &&
            typeof v2FrameAgentKey === "string" &&
            v2FrameAgentKey.startsWith("chat_search"));
        // chat-search-v1 turns get a trailing `suggestions` frame shortly after
        // this completion frame; the socket close below is DEFERRED until it
        // lands (see the deferred-close block).
        const isChatSearchCompleted =
          chatSearchV1Enabled &&
          typeof v2FrameAgentKey === "string" &&
          v2FrameAgentKey.startsWith("chat_search");
        const isContractBChat = isV2 && contractBChatActiveRef.current;
        v2orch("frame.completed", {
          traceId: v2FrameTraceId,
          agentKey: v2FrameAgentKey,
          totalMs: parseMessage?.totalMs,
          reason: parseMessage?.reason,
          isV2,
        });

        if (contractBActiveRef.current && v2FrameAgentKey && lastFinalizedEntryIdRef.current) {
          stampResponder(lastFinalizedEntryIdRef.current, v2FrameAgentKey, v2FrameTraceId);
        }

        if (isContractBChat) {
          v2orch("contractB.chat.completed-skip-finalize", {
            traceId: v2FrameTraceId,
            agentKey: v2FrameAgentKey,
          });
          applyContractBChatCards(parseMessage);
        } else if (isV2) {
          // V2 turn: render from accumulated blocks before the legacy
          // completed-handler runs its cleanup. finalize stamps the in-place
          // pending entry by id — the legacy isLoading sweep below then
          // becomes a no-op on this entry.
          finalizeV2Turn(parseMessage);
        } else {
          // Non-V2 turn: drop the stashed pending entry id so a subsequent V2
          // turn doesn't try to target this turn's bubble. Buffer should
          // already be empty (no v2_block frames came in), but reset
          // defensively.
          if (v2BlockBufferRef.current.length > 0) {
            resetV2Buffer(v2FrameTraceId, "non-v2-completed");
          }
          outboundV2RequestRef.current = null;
        }

        // Dump the assembled final message — for legacy turns this is the
        // concatenated FOLLOW_UP stream-text payload, for V2 it's the
        // renderer's output. Either way it's the exact string that flows into
        // CustomMarkdown.normalizeMarkdown. Use this to debug bullet rendering
        // and other markdown issues by filtering on `[v2-orch] final-message`.
        setChatData((prev: iChatData[]) => {
          const last = prev[prev.length - 1];
          v2orch("final-message", {
            traceId: v2FrameTraceId,
            agentKey: v2FrameAgentKey,
            isV2,
            messageLen: typeof last?.message === "string" ? last.message.length : 0,
            messagePreview:
              typeof last?.message === "string" ? last.message.slice(0, 500) : null,
            preTextLen: typeof last?.preText === "string" ? last.preText.length : 0,
            preTextPreview:
              typeof last?.preText === "string" ? last.preText.slice(0, 200) : null,
            responseType: last?.responseType,
          });
          return prev;
        });

        // chat-search-v1: the chips ride a trailing `suggestions` frame emitted
        // just AFTER this completion frame (so generating them never delays the
        // result cards). Closing the socket here would drop that frame
        // (GoneException). Hold the socket open until it arrives — the
        // `suggestions` handler closes then — with a safety timeout so a
        // never-arriving frame can't leak the connection. All other paths close
        // immediately, unchanged.
        if (isChatSearchCompleted || isContractBChat || frameTurn?.voice) {
          clearSuggestionsWait();
          suggestionsWaitTimerRef.current = setTimeout(() => {
            suggestionsWaitTimerRef.current = null;
            contractBChatActiveRef.current = false;
            handleCloseSocketConnection();
          }, SUGGESTIONS_WAIT_MS);
        } else {
          handleCloseSocketConnection();
        }

        /**
         * Check for missing data in itinerary and regenerate if required.
         * Skipped on poc_chat-routed V2 turns — those don't produce a
         * destinationList; the regenerate-itinerary fallback would otherwise
         * fire and break the V2 entry.
         */
        const itineraryData: any[] =
          !isV2 &&
          Array.isArray(
            Array.isArray(selectedChatData.destinationList) ? selectedChatData.destinationList : []
          )
            ? selectedChatData.destinationList
            : [];
        // let missingData: any = checkForMissingItineraryDataUtil(itineraryData);
        let missingData: any = !isV2 ? checkForMissingItineraryDataUtil(itineraryData) : [];
        consoleLog(["MISSING DATA", missingData]);
        if (missingData?.length > 0) {
          captureException({
            missingData,
          });
          setRegeneratingItinerary(true);
          setSelectedChatData((prev: any) => ({
            ...prev,
            destinationList: prev?.destinationList?.map((ele: iCityDetailsResult) => ({
              ...ele,
              tripDetails: [TEMP_TRIP_DETAILS_DATA, TEMP_TRIP_DETAILS_DATA, TEMP_TRIP_DETAILS_DATA],
            })),
          }));
          if (streamingFlagAndResponseType?.chatId === "")
            setChatData((prev: any) => [
              ...prev.slice(0, -1),
              {
                id: v4(),
                message: "",
                prompt: [],
                isUserMessage: false,
                isLoading: true,
                destinationList: [],
                singleSelectedDestination: false,
              },
            ]);
          const missingDataPromptLocal = `<missing-data-comma-seperated> are missed in the itinerary. please add all the fields which i have mentioned in the system prompt for the day-by-day-itinerary.`;
          // const missingDataPrompt = `${missingData[0]?.missingFields.join()} are missed in the itinerary. please add all the fields which i have mentioned in the system prompt for the day-by-day-itinerary.`
          const prompt =
            promptTemplate?.find((item: any) => item?.key === "regenerating.prompt")?.value ?? missingDataPromptLocal;
          handleOpenSocketConnection({
            chatId: getChatId() || "",
            messages: parseMessage?.data?.messages,
            iteneryInput: null,
            userInput: getRegeneratingPrompt(prompt, missingData[0]?.missingFields ?? []),
            userLocation: null,
          });
          return;
        }
        setRegeneratingItinerary(false);
        /** END of regenearting code */

        clearTimeout(streamingTimeout.current); // clear the set timeout
        setOpenOverlayScreen(false);
        setDisableInput(false);
        setChatInputText("");
        updateRateLimitCount();
        setStreamingFlagAndResponseType({
          isStillStreamingFlag: false,
          chatId: "",
          responseType: "",
          showMoreClickFlag: false,
        });

        /**
         * For setting the itinerary to the destination list
         */
        if (streamingFlagAndResponseType?.chatId) {
          const updatedDatesSelectedChatData: any = {
            ...selectedChatData,
            destinationList: updateDaywiseTripDateForMetaData(
              selectedChatData?.destinationList,
              getStartDateForItinerary(selectedChatData?.start_date)
            ),
          };
          setChatData((prev: iChatData[]) =>
            prev.map((ele: iChatData) => {
              if (ele.id === streamingFlagAndResponseType?.chatId)
                return {
                  ...ele,
                  destinationList: ele?.destinationList?.map((eleDestination: any) => {
                    if (eleDestination?.checkedFlag)
                      return {
                        ...eleDestination,
                        ...updatedDatesSelectedChatData,
                        checkedFlag: false,
                        checkFlag: false,
                      };
                    return {
                      ...eleDestination,
                    };
                  }),
                };
              else
                return {
                  ...ele,
                  destinationList: ele.destinationList.map((chatDataEle: iCityDetailsResult) => ({
                    ...chatDataEle,
                    checkedFlag: false,
                  })),
                };
            })
          );
          if (
            !selectedChatData?.destinationList?.[0]?.tripDetails?.[0]?.itinerary_by_time_of_the_day?.[0]?.description
          ) {
            setChatData((prev: any) => [
              ...prev.slice(0, -1),
              {
                ...prev[prev.length - 1],
                prompt: parseMessage?.data?.messages?.filter((ele: any) => ele.content !== "") ?? [],
              },
            ]);
          }
        }
        /**
         * check for incomplete itinerary
         */
        setChatData((prev: any) => {
          return prev;
        });

        setChatData((prev: any) =>
          prev.map((ele: iChatData) => ({
            ...ele,
            isLoading: false,
          }))
        );
        // Decouple: turn finished — drop the streaming-card id (POST_TEXT
        // normally already cleared it; defensive for paths that don't emit one).
        streamingItineraryCardIdRef.current = "";
        // V2 turns never set streamingFlagAndResponseType.responseType (the
        // intent envelope's RETURN_TYPE frame is the only thing that sets it,
        // and V2 doesn't emit one). Without this `|| isV2` bypass, the V2
        // entry would never be persisted to history.
        if (streamingFlagAndResponseType?.responseType === "" && !isV2) return;
        const saveMethod =
          chatData?.length === 2 && !streamingFlagAndResponseType?.showMoreClickFlag
            ? streamingFlagAndResponseType?.chatId === ""
              ? "POST"
              : "PUT"
            : "PUT";
        setCallSaveHistoryAPIFlag({
          flag: true,
          method: saveMethod,
        });
        v2orch("history.save", {
          traceId: parseMessage?.traceId,
          method: saveMethod,
          chatDataLen: chatData?.length,
          isV2,
        });
      }
    }
  }, [lastMessage, readyState]);

  const { setCallSaveHistoryAPIFlag, currentHistoryId, patchHistoryTitle }: iHistoryContext = useHistoryContext();

  const { navigateWithParams } = useNavigationUrl();
  const chatAbortController = new AbortController();
  const chatAbortControllerRef = useRef<AbortController | null>(null);
  const [disableInput, setDisableInput] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [callPriceAPIFlag, setCallPriceAPIFlag] = useState({
    false: false,
    payload: { cities: [] },
  });

  function userAbortChat() {
    return;
  }

  function extractCityNames(data: any) {
    const cityNames = data.map((item: any) => item?.city_name ?? " ");
    let result =
      cityNames.length > 1 ? cityNames.slice(0, -1).join(", ") + " and " + cityNames.slice(-1) : cityNames[0];
    return result;
  }

  let initialReturnType = "";
  async function handleShowmoreEvents(value: any): Promise<void> {
    if (value?.type === "error") {
      streamingItineraryCardIdRef.current = "";
      toastMessage.error(value?.message ?? "Something went wrong");
      handleCloseSocketConnection();
      setStreamingFlagAndResponseType({
        isStillStreamingFlag: false,
        responseType: "",
        chatId: "",
        showMoreClickFlag: false,
      });
      setOpenLoaderOverlay(false);
      setOpenOverlayScreen(false);
      setDisableInput(false);
      setChatData((prev: iChatData[]) =>
        prev.map((chatDataEle: iChatData) => {
          if (chatDataEle.isLoading)
            return {
              ...chatDataEle,
              isLoading: false,
              destinationList: chatDataEle?.destinationList?.filter(
                (ele: iCityDetailsResult) => ele?.destination_name !== ""
              ),
            };
          else return chatDataEle;
        })
      );
      return;
    }
    if (value?.status === "completed") {
      setChatData((prev: iChatData[]) =>
        prev.map((chatDataEle: any) => {
          if (chatDataEle.isLoading)
            return {
              ...chatDataEle,
              prompt: [
                ...chatDataEle?.prompt,
                {
                  role: "user",
                  content: "Show more",
                },
                {
                  role: "assistant",
                  content: value?.data?.result ?? "",
                },
              ],
            };
          else return chatDataEle;
        })
      );
    }
    try {
      let parseValue: iStreamingResponse = parseStringToJson(value?.data);
      consoleLog(["SHOW MORE EVENTS", value]);
      if (parseValue?.return_type) {
        initialReturnType = parseValue?.return_type;
        if (chatAbortController.signal.aborted) {
          setChatData((prev: iChatData[]) =>
            prev.map((chatDataEle: iChatData) => {
              if (chatDataEle.isLoading)
                return {
                  ...chatDataEle,
                  isLoading: false,
                };
              else return chatDataEle;
            })
          );
          return;
        }

        setSelectedChatData(SELECTED_CHAT_DATA);
      }

      consoleLog([parseValue, "Connected listening events now", initialReturnType]);
      if (
        /**
         * For Destination list
         */
        (parseValue?.intent === DATA_STREAMING_KEY.INTENT.RETURN_TYPE &&
          parseValue?.return_type === DATA_STREAMING_KEY.REUTRN_TYPE.DESTINATIONS) ||
        initialReturnType === "" ||
        initialReturnType === DATA_STREAMING_KEY.REUTRN_TYPE.DESTINATIONS
      ) {
        if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
        else setFirstScreen(ScreenTypes.topDestination);
        switch (parseValue?.intent) {
          case DATA_STREAMING_KEY.INTENT.FOLLOW_UP: {
            if (chatAbortController.signal.aborted) {
              setChatData((prev: iChatData[]) =>
                prev.map((chatDataEle: iChatData) => {
                  if (chatDataEle.isLoading)
                    return {
                      ...chatDataEle,
                      isLoading: false,
                    };
                  else return chatDataEle;
                })
              );
              return;
            }
            setChatData((prev: iChatData[]) =>
              prev.map((chatDataEle: iChatData) => ({
                ...chatDataEle,
                destinationList: chatDataEle?.destinationList?.filter(
                  (ele: iCityDetailsResult) => ele?.destination_name !== ""
                ),
              }))
            );
            setChatData((prev: any) => [
              ...prev,
              {
                id: v4(),
                message: parseValue?.statement,
                prompt: [],
                isUserMessage: false,
                isLoading: false,
                destinationList: [],
                singleSelectedDestination: false,
              },
            ]);
            break;
          }
          case DATA_STREAMING_KEY.INTENT.PRE_TEXT: {
            if (chatAbortController.signal.aborted) {
              setChatData((prev: iChatData[]) =>
                prev.map((chatDataEle: iChatData) => {
                  if (chatDataEle.isLoading) {
                    return {
                      ...chatDataEle,
                      // isLoading: false,
                    };
                  } else return chatDataEle;
                })
              );
              return;
            }
            setChatData((prev: iChatData[]) =>
              prev.map((chatDataEle: iChatData) => {
                // if (chatDataEle.id === chatDataArgs.id)
                if (chatDataEle.isLoading) {
                  return {
                    ...chatDataEle,
                    preText: parseValue?.statement,
                  };
                } else return chatDataEle;
              })
            );
            break;
          }
          case DATA_STREAMING_KEY.INTENT.DESTINATION: {
            const destinationSlotId = v4();

            setChatData((prev: any) => {
              return prev.map((chatDataEle: iChatData) => {
                if (chatDataEle.isLoading) {
                  let tempDestinationList: any = chatDataEle.destinationList;
                  for (let i = 0; i < tempDestinationList.length; i++) {
                    if (!tempDestinationList[i].destination_name) {
                      tempDestinationList[i] = {
                        ...parseValue,
                        checkedFlag: false,
                        checkFlag: false,
                        id: destinationSlotId,
                        imageURL: "",
                      };
                      break;
                    }
                  }
                  return {
                    ...chatDataEle,
                    destinationList: tempDestinationList,
                  };
                } else return chatDataEle;
              });
            });

            {
              const cacheCity = parseValue?.destination_city || "";
              const cacheCountry = parseValue?.country_code || "";
              getItineraryImageURL(accessJWT, cacheCity, cacheCountry || undefined, CHAT_DESTINATION_IMAGE_COUNT, imageSource)
                .then((imageResponse: any) => {
                  const rawUrls: any = imageResponse?.data?.images;
                  const rawAttr: any = imageResponse?.data?.attributions;
                  const urls: string[] = Array.isArray(rawUrls)
                    ? rawUrls.filter((u: any) => typeof u === "string" && u.length > 0)
                    : [];
                  const attributions = Array.isArray(rawAttr)
                    ? urls.map((_, i) => rawAttr[i] ?? null)
                    : urls.map(() => null);
                  if (cacheCity && urls.length > 0) {
                    setCityImageCache(cityImageCacheKey(cacheCity, cacheCountry), {
                      urls,
                      attributions,
                    });
                  }
                  const imageURL = urls[0] || imageResponse?.data?.image;
                  const imageAttribution = attributions[0] ?? null;
                  if (!imageURL) return;
                  setChatData((prev: any) =>
                    prev.map((chatDataEle: iChatData) => ({
                      ...chatDataEle,
                      destinationList: chatDataEle?.destinationList?.map((slot: any) =>
                        slot?.id === destinationSlotId ? { ...slot, imageURL, imageAttribution } : slot
                      ),
                    }))
                  );
                })
                .catch(() => {});
            }
            break;
          }
          case DATA_STREAMING_KEY.INTENT.POST_TEXT: {
            setChatData((prev: iChatData[]) =>
              prev.map((chatDataEle: iChatData) => {
                if (chatDataEle.isLoading)
                  return {
                    ...chatDataEle,
                    // isLoading: false,
                    postText: parseValue?.statement,
                    destinationList: chatDataEle?.destinationList?.filter(
                      (ele: iCityDetailsResult) => ele?.destination_name
                    ),
                  };
                else return chatDataEle;
              })
            );
            break;
          }
          default: {
            break;
          }
        }
      }
    } catch (error: any) {
      if (chatAbortController.signal.aborted) userAbortChat();
      setChatData((prev: iChatData[]) =>
        prev.map((chatDataEle: iChatData) => {
          if (chatDataEle.isLoading)
            return {
              ...chatDataEle,
              isLoading: false,
              destinationList: chatDataEle?.destinationList?.filter(
                (ele: iCityDetailsResult) => ele?.destination_name !== ""
              ),
            };
          else return chatDataEle;
        })
      );
      handleCloseSocketConnection();
      setStreamingFlagAndResponseType({
        isStillStreamingFlag: false,
        responseType: "",
        chatId: "",
        showMoreClickFlag: false,
      });
      setOpenLoaderOverlay(false);
      setOpenOverlayScreen(false);
      setDisableInput(false);
      if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
      else {
        setFirstScreen(ScreenTypes.topDestination);
        setScreen("");
      }
      consoleLog([value, "PARSING ERROR", error.message]);
      return;
    }
  }

  async function handleFollowUpStreamEvents(value: any): Promise<void> {
    try {
      consoleLog(["SOCKET Stream FOLLOW UP EVENTS", value]);
      setChatData((prevChatData: any) => [
        ...prevChatData.slice(0, -1),
        {
          id: v4(),
          message: prevChatData[prevChatData.length - 1].message + value,
          prompt: [],
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
      ]);
    } catch (error: any) {}
  }

  function getItineraryVersion(verion: any) {
    try {
      const numberVersion = Number(verion.split(".")[0]);
      return typeof numberVersion === "number" ? numberVersion : 1;
    } catch (err: any) {
      return 1;
    }
  }
  // function extractStatement(str: string) {
  //   const match = str.match(/"statement":\s*"([^"]*)"/); // Match statement and handle newlines manually
  //   return match ? match[1].replace(/\\n/g, "\n") : null; // Replace escaped newlines with actual newlines
  // }
  async function handleConversationStreamEvents(values: any, streamingFlag?: any): Promise<void> {
    // const test: any = {
    //   type: "stream",
    //   section: "destination-4",
    //   data: '{{"intent": "destination", "country_code": "NP", "destination_name": "Pokhara", "destination_city": "Pokhara, Gandaki Province", "destination_description": "Gateway to the Annapurnas with stunning lake views, adventure sports, and incredible mountain trekking opportunities", "approximate_trip_cost": "$350-600 per person for 5-6 days"}',
    // };
    // if (values?.section === "day-3-plan")
    //   handleCloseSocketConnection()
    let value: any = values;
    try {
      /*
       * START Event
       */
      consoleLog(["SOCKET Stream Conversation EVENTS", value]);
      if (
        value?.type === DATA_STREAMING_KEY.EVENTS.STREAM &&
        value?.section === DATA_STREAMING_KEY.EVENTS.RETURN_TYPE
      ) {
        const repaired = jsonrepair(value?.data);
        const parseData = safeParseJSON(repaired);
        const returnType = parseData?.return_type;
        setStreamingFlagAndResponseType((prev: any) => ({
          ...prev,
          isStillStreamingFlag: true,
          responseType: returnType,
        }));
        if (
          returnType === DATA_STREAMING_KEY.REUTRN_TYPE.FOLLOW_UP &&
          !location.pathname.startsWith(ROUTES.onBoarding)
        ) {
          // Keep an open itinerary panel visible while the user chats. Only
          // "Back to Chat" or an itinerary-modifying return-type (ITINERARY)
          // should dismiss it; a plain follow-up answer must not.
          const isItineraryOpen =
            screen === ScreenTypes.SingleItinerary || screen === ScreenTypes.ItineraryInfo;
          if (!isItineraryOpen) {
            if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
            else {
              setScreen("");
              setFirstScreen(ScreenTypes.topDestination);
            }
          }
        } else if (returnType === DATA_STREAMING_KEY.REUTRN_TYPE.BOOKING) {
          if (!regeneratingItinerary) setSelectedChatData(SELECTED_CHAT_DATA);
          setStreamingFlagAndResponseType((prev: any) => ({
            ...prev,
            isStillStreamingFlag: true,
            responseType: returnType,
            booking: true,
          }));
        } else if (returnType === DATA_STREAMING_KEY.REUTRN_TYPE.ITINERARY) {
          if (!regeneratingItinerary) setSelectedChatData(SELECTED_CHAT_DATA);
        } else if (returnType === DATA_STREAMING_KEY.REUTRN_TYPE.DESTINATIONS) {
          if (streamingFlagAndResponseType?.showMoreClickFlag === true) return;
          const destList = {
            destination_name: "",
            destination_city: "",
            destination_country: "",
            destination_description: "",
            approximate_trip_cost: "",
            averagePackagePriceForExperiencing: "",
            description: "",
            id: "",
            checkFlag: false,
            locations: [],
            imageURL: "",
            checkedFlag: false,
          };
          setChatData((prev: any) => [
            ...prev.slice(0, -1),
            {
              ...CHAT_DATA,
              destinationList: CHAT_DATA.destinationList.map((ele: any) => destList),
              preText: "",
              id: v4(),
            },
          ]);
        }
        return;
      }
      /**
       * LLM ERROR
       */
      if (value?.type === DATA_STREAMING_KEY.ERROR) {
        streamingItineraryCardIdRef.current = "";
        setOpenLoaderOverlay(false);
        setStreamingFlagAndResponseType({
          chatId: "",
          isStillStreamingFlag: false,
          responseType: "",
          showMoreClickFlag: false,
        });
        setChatData((prev: any) => [
          ...prev.slice(0, -1),
          {
            ...prev.slice(-1)[0],
            isLoading: false,
            message:
              values?.reason === "empty_user_message"
                ? "I didn't quite catch that. Could you rephrase what you'd like me to do?"
                : values?.message || LLM_RESPONSE_ERROR.jsonError.message,
            isProcessInterrupted: values?.reason !== "empty_user_message",
            isErrorMessage: true,
          },
        ]);
        if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
        else {
          setScreen("");
          setFirstScreen(ScreenTypes.topDestination);
        }
        setDisableInput(false);
        setOpenOverlayScreen(false);
        setChatInputText("");
        voiceBus.requestFailed(lastMessage?.turn);
        handleCloseSocketConnection();
        return;
      }
      /**
       * END Event
       */
      if (
        value?.type === DATA_STREAMING_KEY.EVENTS.STATUS &&
        value?.status === DATA_STREAMING_KEY.EVENTS.COMPLETED &&
        value?.data !== DATA_STREAMING_KEY.EVENTS.DONE
      ) {
        if (destinationListGeneratedGAEventsFlag) {
          triggerGAEvent(
            GA_LABEL_ENUMS.home_four_trip_suggestions_generated,
            "",
            "",
            "",
            destinationListCityGAEventsCustomObjRef?.current
          );
          setDestinationListGeneratedGAEventsFlag(false);
        }
        setOpenLoaderOverlay(false);
        setStreamingFlagAndResponseType({
          chatId: "",
          isStillStreamingFlag: false,
          responseType: "",
          showMoreClickFlag: false,
        });
        const results = value?.data?.result ?? value?.data?.results ?? "";
        setSelectedChatData((prev: any) => {
          return {
            ...prev,
            results: results,
          };
        });

        // Stamp orchestrator trace on the finalized entry. `agent_key` is
        // set by the Lambda on every outbound frame when orchestrator-routing
        // is on; non-orchestrated traffic leaves it undefined and the stamp
        // is omitted entirely (the entry stays untouched, matching today's
        // behavior on dev/prod).
        const legacyAgentKey: string | undefined = value?.agent_key;
        const legacyTraceId: string | undefined = value?.traceId;
        const legacyFeSentTraceId = outboundV2RequestRef.current?.traceId;
        if (legacyFeSentTraceId && legacyTraceId && legacyFeSentTraceId !== legacyTraceId) {
          v2orchErr("traceId-mismatch", {
            feSent: legacyFeSentTraceId,
            lambdaStamped: legacyTraceId,
            agentKey: legacyAgentKey,
            note: "Persisting Lambda-stamped traceId on legacy entry; FE-sent value discarded.",
          });
        }
        const legacyAgentStamp = legacyAgentKey
          ? {
              agents: [{ key: legacyAgentKey }],
              router: { key: "poc_orchestration" },
              ...(legacyTraceId ? { traceId: legacyTraceId } : {}),
            }
          : {};

        if (streamingFlagAndResponseType?.chatId === "") {
          consoleLog(["REAL RESULT", results, value]);
          if (streamingFlagAndResponseType?.responseType === "destinations")
            setChatData((prev: any) => [
              ...prev?.slice(0, -1),
              {
                ...prev?.slice(-1)[0],
                prompt: Array.isArray(value?.data?.messages)
                  ? value.data.messages.filter((ele: any) => ele.content !== "")
                  : prev?.slice(-1)[0]?.prompt ?? [],
                results: results,
                // message: extractStatement(results),
                destinationList: prev
                  ?.slice(-1)[0]
                  ?.destinationList?.filter((eleDestList: iCityDetailsResult) => eleDestList?.destination_name),
                ...legacyAgentStamp,
              },
            ]);
          else
            setChatData((prev: any) => [
              ...prev?.slice(0, -1),
              {
                ...prev?.slice(-1)[0],
                prompt: Array.isArray(value?.data?.messages)
                  ? value.data.messages.filter((ele: any) => ele.content !== "")
                  : prev?.slice(-1)[0]?.prompt ?? [],
                results: results,
                // message: extractStatement(results),
                ...legacyAgentStamp,
              },
            ]);
          consoleLog("SOCKET END EVENT. STOP");
        }
        if (legacyAgentKey) {
          v2orch("legacy.stamp", {
            traceId: legacyTraceId,
            agentKey: legacyAgentKey,
            chatId: streamingFlagAndResponseType?.chatId,
            router: "poc_orchestration",
          });
        }
        return;
      }

      /**
       * FOLLOW UP
       */
      if (
        streamingFlagAndResponseType?.responseType.split("-").join("") === DATA_STREAMING_KEY.REUTRN_TYPE.FOLLOW_UP &&
        value?.type.split("-").join("") === DATA_STREAMING_KEY.REUTRN_TYPE.FOLLOW_UP
      ) {
        setChatInputText("");

        if (!openLoaderOverlay) {
          setChatData((prevChatData: any) => {
            const last = prevChatData[prevChatData.length - 1];
            return [
              ...prevChatData.slice(0, -1),
              {
                ...last,
                id: last?.id ?? v4(),
                message: (last?.message ?? "") + value.data,
                prompt: [],
                isUserMessage: false,
                isLoading: false,
                destinationList: [],
                singleSelectedDestination: false,
              },
            ];
          });
        } else {
          setChatData((prevChatData: any) => {
            const last = prevChatData[prevChatData.length - 1];
            return [
              ...prevChatData,
              {
                id: v4(),
                message: (last?.message ?? "") + value.data,
                prompt: [],
                isUserMessage: false,
                isLoading: false,
                destinationList: [],
                singleSelectedDestination: false,
              },
            ];
          });
          if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
          else {
            setScreen("");
            setFirstScreen(ScreenTypes.topDestination);
          }
          setOpenLoaderOverlay(false);
          setDisableInput(false);
        }
        if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
        else {
          setScreen("");
          setFirstScreen(ScreenTypes.topDestination);
        }
        setOpenLoaderOverlay(false);
        setDisableInput(false);
        return;
      } else if (streamingFlagAndResponseType?.responseType === DATA_STREAMING_KEY.REUTRN_TYPE.BOOKING ||
                 streamingFlagAndResponseType?.responseType === DATA_STREAMING_KEY.REUTRN_TYPE.ITINERARY) {
        /**
         * ITINERARY / BOOKING
         */
        if (value?.data === DATA_STREAMING_KEY.EVENTS.DONE) return;

        let parseValue = JSON?.parse(value?.data);
        switch (parseValue?.intent) {
          case DATA_STREAMING_KEY.INTENT.TRIP_PREFERENCE: {
            setSelectedChatData((prev: any) => ({
              ...prev,
              noOfDays: parseValue?.number_of_travel_days,
              number_of_days: parseValue?.number_of_travel_days,
              numberOfdays: parseValue?.number_of_travel_days,
              ...parseValue,
              from_latitude: parseValue?.departure?.city?.latitude,
              from_longitude: parseValue?.departure?.city?.longitude,
              start_date: parseValue?.departure?.date,
              trip_type: parseValue?.flights?.trip_type,
              from:
                parseValue?.departure?.city?.name +
                ", " +
                parseValue?.departure?.city?.state +
                ", " +
                parseValue?.departure?.city?.country,
              fromCity:
                parseValue?.departure?.city?.name +
                ", " +
                parseValue?.departure?.city?.state +
                ", " +
                parseValue?.departure?.city?.country,
              flight_cabin_class: parseValue?.flights?.cabin_class,
              number_of_stops: parseValue?.flights?.number_of_stops,
              flight_refundable: parseValue?.flights?.refundable,
              flight_day_part: parseValue?.flights?.time_of_day,
              departure_airport: parseValue?.flights?.departure_airports,
              arrival_airport: parseValue?.flights?.arrival_airports,
              airlines: parseValue?.flights?.airlines,

              number_of_rooms: parseValue?.hotels?.number_of_rooms,
              hotel_refundable: parseValue?.hotels?.refundable,
              hotel_star_ratings: parseValue?.hotels?.star_ratings,
              hotels: parseValue?.hotels?.hotel_brands ?? [],
              hotel_name: parseValue?.hotels?.hotel_name ?? "",
              // Surface accommodation_type from the LLM trip-preferences as a
              // top-level field. The line above overwrites
              // selectedChatData.hotels with just the brands array, so the
              // nested path no longer holds it.
              accommodation_type: parseValue?.hotels?.accommodation_type ?? "",
              id: v4(),
            }));
            break;
          }
          case DATA_STREAMING_KEY.INTENT.ITINERARY_SUMMARY: {
            setStreamingFlagAndResponseType((prev) => ({
              ...prev,
              booking: false,
            }));
            setChatInputText("");
            if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding))
              navigateWithParams(ROUTES.ItineraryDetails);
            else {
              setScreen(ScreenTypes.ItineraryInfo);
              setFirstScreen(ScreenTypes.topDestination);
            }
            setOpenLoaderOverlay(false);
            parseValue = {
              ...parseValue,
              ...removeDuplicateDays(parseValue?.destinations),
            };

            const destinationList: any = await Promise.all(
              parseValue?.destinations?.map(async (ele: any) => {
                const cityName = ele?.city_name;
                const noOfDays = ele?.no_of_day?.length || 0;
                const cityCountry = ele?.city_country;
                // const imageResponse: any = await getItineraryImageURL(accessJWT, cityName || "");
                let tempTripDetails: iTripDetails[] = [];
                for (let i = 0; i < noOfDays; i++) {
                  tempTripDetails.push({
                    ...TEMP_TRIP_DETAILS_DATA,
                    day: "Day " + ele?.no_of_day[i],
                  });
                }
                return {
                  ...DESTINATION_LIST_DATA,
                  city_name: cityName,
                  city_country_code: ele?.country_code,
                  city_country: cityCountry,
                  tripDetails: tempTripDetails.map((eleTripDetails: iTripDetails) => ({
                    ...eleTripDetails,
                    city_name: cityName,
                  })),
                };
              })
            );

            setSelectedChatData((prev: any) => ({
              ...prev,
              id: streamingFlagAndResponseType?.chatId || prev?.id,
              description: parseValue?.description ?? "",
              singleCity: parseValue?.destinations?.length === 1 ? true : false,
              singleSelectedDestination: true,
              complete_itinerary_description: parseValue?.description,
              number_of_days: parseValue?.number_of_days,
              noOfDays: parseValue?.number_of_days,
              numberOfdays: parseValue?.number_of_days,
              destinationList,
              itinerary_version: parseValue?.version ? getItineraryVersion(parseValue?.version ?? "1") : 1,
              complete_itinerary_flights_cost: "$" + parseValue?.flights_cost,
              complete_itinerary_hotels_cost: "$" + parseValue?.hotels_cost,
              tripName: extractCityNames(parseValue?.destinations),
            }));

            const destinationListImage: any = await Promise.all(
              parseValue?.destinations?.map(async (ele: any) => {
                const cityName = ele?.city_name || "";
                const countryCode = ele?.country_code || "";
                const cacheHit = getCityImageCache(cityImageCacheKey(cityName, countryCode));
                if (cacheHit && cacheHit.urls.length > 0) {
                  return { imageURL: cacheHit.urls[0] };
                }
                const imageResponse: any = await getItineraryImageURL(
                  accessJWT,
                  cityName,
                  countryCode || undefined,
                  CHAT_DESTINATION_IMAGE_COUNT,
                  imageSource
                );
                const rawUrls: any = imageResponse?.data?.images;
                const rawAttr: any = imageResponse?.data?.attributions;
                const urls: string[] = Array.isArray(rawUrls)
                  ? rawUrls.filter((u: any) => typeof u === "string" && u.length > 0)
                  : [];
                const attributions = Array.isArray(rawAttr)
                  ? urls.map((_, i) => rawAttr[i] ?? null)
                  : urls.map(() => null);
                if (cityName && urls.length > 0) {
                  setCityImageCache(cityImageCacheKey(cityName, countryCode), {
                    urls,
                    attributions,
                  });
                }
                return {
                  imageURL: urls[0] || imageResponse?.data?.image,
                };
              })
            );
            if (chatAbortController.signal.aborted) userAbortChat();
            const pricePayload: any = {
              cities: getItineraryPriceCityList(destinationList),
            };
            setCallPriceAPIFlag({
              false: true,
              payload: pricePayload ?? { cities: [] },
            });

            setSelectedChatData((prev: any) => ({
              ...prev,
              destinationList: prev.destinationList.map((eleDestinationList: iCityDetailsResult, index: number) => ({
                ...eleDestinationList,
                imageURL: destinationListImage[index]?.imageURL,
              })),
            }));

            // Decouple (chat path only): materialize the itinerary card into
            // chatData NOW (skeleton days + summary) so it has its own id and
            // survives the user switching to a previous card while generation
            // continues. Card-click path (chatId set) keeps today's behavior:
            // the card materializes in the COMPLETED block keyed on chatId.
            // isLoading:true is kept so the timeout/abort sweeps still treat it
            // as evictable; the card still RENDERS because the SelectedTripNew
            // gate is singleSelectedDestination, not isLoading (and the
            // LLMLoader is gated on !singleSelectedDestination, so no
            // double-render). selectedChatData.id and the card id are both
            // cardId, so the selected-border check stays aligned.
            if (!streamingFlagAndResponseType?.chatId) {
              const cardId = v4();
              streamingItineraryCardIdRef.current = cardId;
              setSelectedChatData((prev: any) => {
                setChatData((prevChatData: any) => {
                  if (prevChatData.length > 0 && prevChatData[prevChatData.length - 1]?.isLoading) {
                    return [...prevChatData.slice(0, -1), { ...prev, id: cardId, isLoading: true }];
                  }
                  return prevChatData;
                });
                return { ...prev, id: cardId };
              });
            }
            break;
          }
          case DATA_STREAMING_KEY.INTENT.BOOKING: {
            setStreamingFlagAndResponseType((prev) => ({
              ...prev,
              booking: true,
            }));
            setChatInputText("");
            if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.BookingLoader);
            else {
              setScreen(ScreenTypes.BookingLoader);
              setFirstScreen(ScreenTypes.topDestination);
            }
            setOpenLoaderOverlay(false);
            parseValue = {
              ...parseValue,
              ...removeDuplicateDays(parseValue?.destinations),
            };

            const destinationList: any = await Promise.all(
              parseValue?.destinations?.map(async (ele: any) => {
                const cityName = ele?.city_name;
                const noOfDays = ele?.no_of_day?.length || 0;
                const cityCountry = ele?.city_country;
                let tempTripDetails: iTripDetails[] = [];
                for (let i = 0; i < noOfDays; i++) {
                  tempTripDetails.push({
                    ...TEMP_TRIP_DETAILS_DATA,
                    day: "Day " + ele?.no_of_day[i],
                  });
                }
                return {
                  ...DESTINATION_LIST_DATA,
                  city_name: cityName,
                  city_country_code: ele?.country_code,
                  city_country: cityCountry,
                  tripDetails: tempTripDetails.map((eleTripDetails: iTripDetails) => ({
                    ...eleTripDetails,
                    city_name: cityName,
                  })),
                };
              })
            );

            setSelectedChatData((prev: any) => ({
              ...prev,
              id: streamingFlagAndResponseType?.chatId || prev?.id,
              description: parseValue?.description ?? "",
              singleCity: parseValue?.destinations?.length === 1 ? true : false,
              singleSelectedDestination: true,
              complete_itinerary_description: parseValue?.description,
              number_of_days: parseValue?.number_of_days,
              noOfDays: parseValue?.number_of_days,
              numberOfdays: parseValue?.number_of_days,
              destinationList,
              itinerary_version: parseValue?.version ? getItineraryVersion(parseValue?.version ?? "1") : 1,
              complete_itinerary_flights_cost: "$" + parseValue?.flights_cost,
              complete_itinerary_hotels_cost: "$" + parseValue?.hotels_cost,
              tripName: extractCityNames(parseValue?.destinations),
              isBooking: true,
            }));

            const destinationListImage: any = await Promise.all(
              parseValue?.destinations?.map(async (ele: any) => {
                const cityName = ele?.city_name;
                const imageResponse: any = await getItineraryImageURL(accessJWT, cityName || "");

                return {
                  imageURL: imageResponse?.data?.image,
                };
              })
            );
            if (chatAbortController.signal.aborted) userAbortChat();
            const pricePayload: any = {
              cities: getItineraryPriceCityList(destinationList),
            };
            setCallPriceAPIFlag({
              false: true,
              payload: pricePayload ?? { cities: [] },
            });

            setSelectedChatData((prev: any) => ({
              ...prev,
              destinationList: prev.destinationList.map((eleDestinationList: iCityDetailsResult, index: number) => ({
                ...eleDestinationList,
                imageURL: destinationListImage[index]?.imageURL,
              })),
            }));
            break;
          }
          case DATA_STREAMING_KEY.INTENT.PRE_TEXT: {
            if (chatAbortController.signal.aborted) userAbortChat();
            const cardId = streamingItineraryCardIdRef.current;
            if (cardId) {
              // Rare: PRE_TEXT after the card materialized — write to the card
              // by id and mirror to the panel only if viewing it.
              setChatData((prev: any) =>
                prev.map((c: iChatData) => (c?.id === cardId ? { ...c, preText: parseValue?.statement } : c))
              );
              setSelectedChatData((prev: any) =>
                prev?.id === cardId ? { ...prev, preText: parseValue?.statement } : prev
              );
            } else {
              setSelectedChatData((prev: iChatData) => ({
                ...prev,
                preText: parseValue?.statement,
              }));
            }
            break;
          }
          case DATA_STREAMING_KEY.INTENT.DAILY_PLAN: {
            if (chatAbortController.signal.aborted) userAbortChat();
            // Per-day fill logic — preserved verbatim from the original
            // setSelectedChatData body, just factored out so it can target the
            // streaming card (by id) and/or the viewed panel.
            const fillDays = (destinationList: any) =>
              destinationList?.map((eleDestinationList: iCityDetailsResult) => ({
                ...eleDestinationList,
                tripDetails: eleDestinationList.tripDetails.map((eleTripDetails: iTripDetails) => {
                  if (Number(parseValue?.day) === Number(eleTripDetails?.day.split(" ")[1])) {
                    const temp_itinerary_by_time_of_the_day = parseValue?.activities?.map((eleActivities: any) => ({
                      ...eleActivities,
                      id: v4(),
                      description: eleActivities?.description,
                      time_of_the_day: eleActivities?.time || eleActivities?.time_of_the_day,
                      activity_type: eleActivities?.type ?? eleActivities?.activity_type,
                    }));
                    return {
                      ...eleTripDetails,
                      date: parseValue?.date,
                      day_itinerary_description: parseValue?.description,
                      itinerary_by_time_of_the_day_data: temp_itinerary_by_time_of_the_day,
                      itinerary_by_time_of_the_day: combineDescriptionsByTime(temp_itinerary_by_time_of_the_day),
                    };
                  }
                  return eleTripDetails;
                }),
              }));

            const cardId = streamingItineraryCardIdRef.current;
            if (cardId) {
              // Chat path: write the day to the streaming card in chatData by
              // id (survives switching away), and mirror to the panel only if
              // the user is currently viewing that same card.
              setChatData((prev: any) =>
                prev.map((c: iChatData) =>
                  c?.id === cardId ? { ...c, destinationList: fillDays(c.destinationList) } : c
                )
              );
              setSelectedChatData((prev: any) =>
                prev?.id === cardId ? { ...prev, destinationList: fillDays(prev.destinationList) } : prev
              );
            } else {
              // Card-click path (chatId set, cardId === ""): keep today's
              // selectedChatData-only behavior.
              setSelectedChatData((prev: iChatData) => ({
                ...prev,
                destinationList: fillDays(prev.destinationList),
              }));
            }
            break;
          }
          case DATA_STREAMING_KEY.INTENT.POST_TEXT: {
            if (chatAbortController.signal.aborted) userAbortChat();
            const cardId = streamingItineraryCardIdRef.current;
            if (cardId) {
              // Chat path: finalize the streaming card in chatData by id
              // (postText + isLoading:false) so it stays put and stops being
              // evictable; mirror postText to the panel only if viewing it.
              // Do NOT churn the card id here — it would break the React key /
              // selected-border alignment and the auto-save PK.
              setChatData((prev: any) =>
                prev.map((c: iChatData) =>
                  c?.id === cardId ? { ...c, postText: parseValue?.statement, isLoading: false } : c
                )
              );
              setSelectedChatData((prev: any) =>
                prev?.id === cardId ? { ...prev, postText: parseValue?.statement } : prev
              );
              streamingItineraryCardIdRef.current = "";
            } else {
              // Card-click path (cardId === ""): today's exact behavior.
              setSelectedChatData((prev: any) => {
                const tempSelectedChatData = {
                  ...prev,
                  postText: parseValue?.statement,
                  id: v4(),
                };
                setChatData((prevChatData) => {
                  if (prevChatData.length > 0) {
                    if (prevChatData[prevChatData.length - 1].isLoading) {
                      return [
                        ...prevChatData.slice(0, -1),
                        {
                          ...tempSelectedChatData,
                        },
                      ];
                    } else {
                      return prevChatData;
                    }
                  }
                  return prevChatData;
                });
                return tempSelectedChatData;
              });
            }
            break;
          }
          case DATA_STREAMING_KEY.INTENT.FOLLOW_UP: {
            if (chatAbortController.signal.aborted) userAbortChat();
            setChatData((prevChatData: any) => {
              const followUpStreamingCardId = streamingItineraryCardIdRef.current;
              const followUpLast = prevChatData?.[prevChatData?.length - 1];
              // Decouple: never clobber the materialized streaming itinerary
              // card. It carries isLoading:true until POST_TEXT, so the id check
              // (not isLoading alone) is the discriminator — when the trailing
              // entry IS the streaming card, append the follow-up bubble after
              // it instead of replacing it. Fail-safe to today's behavior when
              // no card is materialized (followUpStreamingCardId === "").
              if (
                followUpLast?.isLoading === true &&
                (!followUpStreamingCardId || followUpLast?.id !== followUpStreamingCardId)
              ) {
                return [
                  ...prevChatData.slice(0, -1),
                  {
                    id: v4(),
                    message: parseValue?.statement,
                    prompt: [],
                    isUserMessage: false,
                    isLoading: false,
                    destinationList: [],
                    singleSelectedDestination: false,
                  },
                ];
              } else {
                return [
                  ...prevChatData,
                  {
                    id: v4(),
                    message: parseValue?.statement,
                    prompt: [],
                    isUserMessage: false,
                    isLoading: false,
                    destinationList: [],
                    singleSelectedDestination: false,
                  },
                ];
              }
            });
            if (!streamingFlagAndResponseType?.booking) {
              if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding))
                navigateWithParams(ROUTES.TopDestination);
              else {
                setFirstScreen(ScreenTypes.topDestination);
                setScreen("");
              }
              clearTimeout(streamingTimeout.current);
              setOpenOverlayScreen(false);
              setDisableInput(false);
              setChatInputText("");
              updateRateLimitCount();
            }
            break;
          }
          default: {
            break;
          }
        }
      } else if (streamingFlagAndResponseType?.responseType === DATA_STREAMING_KEY.REUTRN_TYPE.DESTINATIONS) {
        setDestinationListGeneratedGAEventsFlag(true);
        /**
         * DESTINATION LIST
         */
        if (value?.data === DATA_STREAMING_KEY.EVENTS.DONE) return;
        let parseValue = safeParseJSON(value.data);
        const destList = {
          destination_name: "",
          destination_city: "",
          destination_country: "",
          destination_description: "",
          approximate_trip_cost: "",
          averagePackagePriceForExperiencing: "",
          description: "",
          id: "",
          checkFlag: false,
          locations: [],
          imageURL: "",
          checkedFlag: false,
        };
        setChatInputText("");
        if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
        else setFirstScreen(ScreenTypes.topDestination);
        switch (parseValue?.intent) {
          case DATA_STREAMING_KEY.INTENT.FOLLOW_UP: {
            if (chatAbortController.signal.aborted) userAbortChat();
            setChatData((prev: any) => [
              ...prev.slice(0, -1),
              {
                id: v4(),
                message: parseValue?.statement,
                prompt: [],
                isUserMessage: false,
                isLoading: false,
                destinationList: [],
                singleSelectedDestination: false,
              },
            ]);
            clearTimeout(streamingTimeout.current);
            if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
            else {
              setFirstScreen(ScreenTypes.topDestination);
              setScreen("");
            }
            setOpenOverlayScreen(false);
            setDisableInput(false);
            setChatInputText("");
            updateRateLimitCount();
            break;
          }
          case DATA_STREAMING_KEY.INTENT.PRE_TEXT: {
            if (chatAbortController.signal.aborted) userAbortChat();
            if (streamingFlagAndResponseType?.showMoreClickFlag)
              setChatData((prev: iChatData[]) =>
                prev.map((chatDataEle: iChatData) => {
                  if (chatDataEle.isLoading) {
                    return {
                      ...chatDataEle,
                      preText: parseValue?.statement,
                    };
                  } else return chatDataEle;
                })
              );
            else
              setChatData((prev: any) => [
                ...prev.slice(0, -1),
                {
                  ...CHAT_DATA,
                  destinationList: CHAT_DATA.destinationList.map((ele: any) => destList),
                  preText: parseValue?.statement,
                  id: v4(),
                },
              ]);
            break;
          }
          case DATA_STREAMING_KEY.INTENT.DESTINATION: {
            const destinationSlotId = v4();

            /** GA EVENTS CUSTOM OBJ */
            if (destinationListCityGAEventsCustomObjRef?.current?.first === "") {
              destinationListCityGAEventsCustomObjRef.current = {
                ...destinationListCityGAEventsCustomObjRef?.current,
                first: parseValue?.destination_city || "",
              };
            } else if (destinationListCityGAEventsCustomObjRef?.current?.second === "") {
              destinationListCityGAEventsCustomObjRef.current = {
                ...destinationListCityGAEventsCustomObjRef?.current,
                second: parseValue?.destination_city || "",
              };
            } else if (destinationListCityGAEventsCustomObjRef?.current?.third === "") {
              destinationListCityGAEventsCustomObjRef.current = {
                ...destinationListCityGAEventsCustomObjRef?.current,
                third: parseValue?.destination_city || "",
              };
            } else if (destinationListCityGAEventsCustomObjRef?.current?.fourth === "") {
              destinationListCityGAEventsCustomObjRef.current = {
                ...destinationListCityGAEventsCustomObjRef?.current,
                fourth: parseValue?.destination_city || "",
              };
            }
            /** **************************** **/

            if (chatAbortController.signal.aborted) userAbortChat();

            if (streamingFlagAndResponseType?.showMoreClickFlag === true) {
              setChatData((prev: any) => {
                let lastChatData = prev[prev.length - 1];
                let tempDestinationList = lastChatData.destinationList;
                for (let i = 0; i < tempDestinationList.length; i++) {
                  if (!tempDestinationList[i].destination_name) {
                    tempDestinationList[i] = {
                      ...parseValue,
                      checkedFlag: false,
                      checkFlag: false,
                      id: destinationSlotId,
                      imageURL: "",
                    };
                    break;
                  }
                }
                return [
                  ...prev.slice(0, -1),
                  {
                    id: v4(),
                    ...lastChatData,
                    destinationList: tempDestinationList,
                  },
                ];
              });
            } else
              setChatData((prev: any) => {
                let lastChatData = prev[prev.length - 1];
                let tempDestinationList = lastChatData?.destinationList;
                for (let i = 0; i < tempDestinationList?.length; i++) {
                  if (!tempDestinationList[i].destination_name) {
                    tempDestinationList[i] = {
                      ...parseValue,
                      checkedFlag: false,
                      checkFlag: false,
                      id: destinationSlotId,
                      imageURL: "",
                    };
                    break;
                  }
                }
                return [
                  ...prev.slice(0, -1),
                  {
                    id: v4(),
                    ...lastChatData,
                    destinationList: tempDestinationList,
                  },
                ];
              });

            {
              const cacheCity = parseValue?.destination_city || "";
              const cacheCountry = parseValue?.country_code || "";
              getItineraryImageURL(accessJWT, cacheCity, cacheCountry || undefined, CHAT_DESTINATION_IMAGE_COUNT, imageSource)
                .then((imageResponse: any) => {
                  const rawUrls: any = imageResponse?.data?.images;
                  const rawAttr: any = imageResponse?.data?.attributions;
                  const urls: string[] = Array.isArray(rawUrls)
                    ? rawUrls.filter((u: any) => typeof u === "string" && u.length > 0)
                    : [];
                  const attributions = Array.isArray(rawAttr)
                    ? urls.map((_, i) => rawAttr[i] ?? null)
                    : urls.map(() => null);
                  if (cacheCity && urls.length > 0) {
                    setCityImageCache(cityImageCacheKey(cacheCity, cacheCountry), {
                      urls,
                      attributions,
                    });
                  }
                  const imageURL = urls[0] || imageResponse?.data?.image;
                  const imageAttribution = attributions[0] ?? null;
                  if (!imageURL) return;
                  setChatData((prev: any) =>
                    prev.map((chatDataEle: iChatData) => ({
                      ...chatDataEle,
                      destinationList: chatDataEle?.destinationList?.map((slot: any) =>
                        slot?.id === destinationSlotId ? { ...slot, imageURL, imageAttribution } : slot
                      ),
                    }))
                  );
                })
                .catch(() => {});
            }
            break;
          }
          case DATA_STREAMING_KEY.INTENT.POST_TEXT: {
            if (chatAbortController.signal.aborted) userAbortChat();
            setChatData((prev: any) => [
              ...prev.slice(0, -1),
              {
                ...prev.slice(-1)[0],
                postText: parseValue?.statement,
              },
            ]);
            break;
          }
          default: {
            break;
          }
        }
      }
      if (value?.type === "stream" && value?.section === "follow-up") {
        const repaired = jsonrepair(value?.data);
        const parseData = safeParseJSON(repaired);
        consoleLog(["HERE IT IS", value, parseData]);
        setChatData((prev: any) => [
          ...prev?.slice(0, -1),
          {
            ...prev?.slice(-1)[0],
            message: parseData?.statement || "",
          },
        ]);
      }
    } catch (error: any) {
      setOpenLoaderOverlay(false);
      captureException({
        error,
        value,
      });
      consoleLog(["ERROR IN LLM RESPONSE", error, value, "HERE"]);
      handleCloseSocketConnection();
      setStreamingFlagAndResponseType({
        isStillStreamingFlag: false,
        responseType: "",
        chatId: "",
        showMoreClickFlag: false,
      });
      setChatData((prevChatData: any) => {
        if (streamingFlagAndResponseType?.showMoreClickFlag)
          return prevChatData.map((ele: iChatData) => ({
            ...ele,
            destinationList: ele?.destinationList?.filter(
              (eleDestList: iCityDetailsResult) => eleDestList?.destination_name
            ),
          }));
        const dupChatData = JSON.parse(JSON.stringify(chatData));
        const reverseChatData = dupChatData.reverse();
        const promptContextChatData = reverseChatData?.find((ele: iChatData) => ele?.prompt?.length > 1);
        let promptContext: any = [];
        if (promptContextChatData?.prompt?.length)
          promptContext = [
            ...promptContextChatData?.prompt,
            {
              role: "user",
              content: chatData[chatData?.length - 2]?.maskedMessage ?? chatData[chatData?.length - 2]?.message,
            },
          ];
        else {
          promptContext = [
            {
              role: "user",
              content: chatData[chatData?.length - 2]?.maskedMessage ?? chatData[chatData?.length - 2]?.message,
            },
          ];
        }
        if (prevChatData[prevChatData.length - 1].isLoading) {
          return [
            ...prevChatData.slice(0, -1),
            {
              id: v4(),
              message: LLM_RESPONSE_ERROR.jsonError.message,
              isUserMessage: false,
              prompt: promptContext?.filter((ele: any) => ele.content !== ""),
              isLoading: false,
              destinationList: [],
              singleSelectedDestination: false,
            },
          ];
        } else {
          if (streamingFlagAndResponseType?.responseType === "destinations")
            return [
              ...prevChatData.slice(0, -1),
              {
                id: v4(),
                message: LLM_RESPONSE_ERROR.jsonError.message,
                isUserMessage: false,
                prompt: promptContext?.filter((ele: any) => ele.content !== ""),
                isLoading: false,
                destinationList: [],
                singleSelectedDestination: false,
              },
            ];
          return [
            ...prevChatData,
            {
              id: v4(),
              message: LLM_RESPONSE_ERROR.jsonError.message,
              isUserMessage: false,
              prompt: promptContext?.filter((ele: any) => ele.content !== ""),
              isLoading: false,
              destinationList: [],
              singleSelectedDestination: false,
            },
          ];
        }
      });
      if (isMobile && !location.pathname.startsWith(ROUTES.onBoarding)) navigateWithParams(ROUTES.TopDestination);
      else {
        setFirstScreen(ScreenTypes.topDestination);
        setScreen("");
      }

      setOpenOverlayScreen(false);
      setDisableInput(false);
      setCallSaveHistoryAPIFlag({
        flag: true,
        method: chatData?.length === 2 ? "POST" : "PUT",
      });
      setChatInputText("");
      updateRateLimitCount();
      return;
    }
  }

  const value = useMemo(
    () => ({
      handleConversationStreamEvents,
      handleShowmoreEvents,
      handleFollowUpStreamEvents,
      disableInput,
      setDisableInput,
      chatAbortControllerRef,
      chatAbortController,
      startSocketStreamingTimer,
      regeneratingItinerary,
      setRegeneratingItinerary,
      outboundV2RequestRef,
      releaseItinerarySelection,
    }),
    [
      handleConversationStreamEvents,
      handleShowmoreEvents,
      handleFollowUpStreamEvents,
      disableInput,
      setDisableInput,
      chatAbortControllerRef,
      chatAbortController,
      startSocketStreamingTimer,
      regeneratingItinerary,
      setRegeneratingItinerary,
      outboundV2RequestRef,
      releaseItinerarySelection,
    ]
  );

  return <DataStreamingContext.Provider value={value}>{children}</DataStreamingContext.Provider>;
};
export const useDataStreamingContext = () => useContext(DataStreamingContext);
